import React, { Component } from "react";
import {
  Row, Col, Card, Button, Spin, Tag, Empty,
  Tabs, Input, notification, Progress, Divider,
  Modal, Form, Select, Radio,
} from "antd";
import {
  HomeOutlined, ThunderboltOutlined, ToolOutlined,
  SmileOutlined, DollarOutlined, FileTextOutlined,
  SendOutlined, RobotOutlined, PlusOutlined,
  CheckCircleOutlined, ReloadOutlined, HeartOutlined,
  SafetyCertificateOutlined, TrophyOutlined, ArrowRightOutlined,
} from "@ant-design/icons";
import { add, update, list, deleteRecord, noAuthGetInfo, noAuthgetDetails } from "../api/Apis";
import Advertisement from "./Advertisement";
import Amplify, { Storage } from "aws-amplify";
import AwsConfig from "../config/AwsConfig";
import ChecklistForm from "./ChecklistForm";
import { withRouter } from "react-router-dom";

Amplify.configure(AwsConfig);

const { TabPane } = Tabs;
const { TextArea } = Input;

// ── Brand tokens ───────────────────────────────────────────────────────────────
const NAVY  = "#1A2280";
const GOLD  = "#D4921A";
const LIGHT = "#F7F9FC";
const BORDER = "#E2E8F0";
const MUTED = "#718096";
const WHITE = "#FFFFFF";

// ── AI proxy — routes through Netlify function to avoid browser CORS block ────
const AI_PROXY = "https://ai.myhomebuddha.com/.netlify/functions/claude";
const AI_MODEL = "claude-haiku-4-5-20251001";

// ── Section config ────────────────────────────────────────────────────────────
const SECTIONS = [
  {
    key: "home_health",
    aiKeys: ["health","watersource","sewer","electric","concerns"],
    label: "Home Health & Safety",
    icon: <HomeOutlined />,
    emoji: "🏥",
    color: "#E53E3E",
    points: [
      "Water source, quality & treatment systems",
      "Sewer / septic system condition",
      "Smoke & CO2 detectors — placement & age",
      "Indoor air quality & ventilation",
      "Electrical panel safety & capacity",
      "Fire risks, radon systems & lead paint",
      "Foundation moisture & structural concerns",
      "Trees, hazards & site safety",
    ],
    subsections: [
      { id:"watersource", emoji:"🚰", label:"Water Source & Quality",      hint:"Well, public water, filtration, softener, water test results" },
      { id:"sewer",       emoji:"🪣", label:"Sewer / Septic",              hint:"Sewer cleanout, septic tank, distribution box, leach field" },
      { id:"smoke_co",    emoji:"🔔", label:"Smoke & CO2 Detectors",       hint:"Location, brand, install date" },
      { id:"air_quality", emoji:"💨", label:"Air Quality & Ventilation",   hint:"HRV/ERV, bathroom fans, crawl space vents, mold concerns" },
      { id:"electric",    emoji:"⚡", label:"Electrical Panel",            hint:"Breakers, capacity, age, any concerns" },
      { id:"radon",       emoji:"☢️", label:"Radon & Hazardous Materials",  hint:"Mitigation system, asbestos, lead paint disclosures" },
      { id:"concerns",    emoji:"⚠️", label:"Areas of Concern",            hint:"Damage, moisture, staining, cracking, safety issues" },
    ],
  },
  {
    key: "energy_efficiency",
    aiKeys: ["energy","attic","windows"],
    label: "Energy Efficiency & Utility Savings",
    icon: <ThunderboltOutlined />,
    emoji: "⚡",
    color: "#D69E2E",
    points: [
      "Air leak areas & draft sources",
      "Insulation & attic effectiveness",
      "Windows & doors — seals, efficiency",
      "Electrical panel, lighting & smart upgrades",
      "Solar panels & renewable options",
      "Utility bill analysis & savings opportunities",
    ],
    subsections: [
      { id:"attic",       emoji:"🔍", label:"Attic & Insulation",         hint:"Insulation depth/type, ventilation, air sealing" },
      { id:"windows",     emoji:"🪟", label:"Windows & Doors",            hint:"Frame condition, glass type, weatherstripping, drafts" },
      { id:"air_sealing", emoji:"🌬️", label:"Air Sealing & Drafts",       hint:"Penetrations, rim joists, attic hatches, outlets" },
      { id:"solar",       emoji:"☀️", label:"Solar & Renewables",         hint:"Panels, inverter, battery storage, EV charger" },
      { id:"utility",     emoji:"📊", label:"Utility Bills & Meters",     hint:"Electric, gas, water usage — attach recent bills" },
    ],
  },
  {
    key: "maintenance_longevity",
    aiKeys: ["maintenance","roof","hvac","hwheater","plumbing","foundation","appliances"],
    label: "Maintenance, Repair & Longevity",
    icon: <ToolOutlined />,
    emoji: "🔧",
    color: "#2B6CB0",
    points: [
      "Roof condition, age & drainage",
      "Furnace / heating system performance",
      "Hot water heater — age & efficiency",
      "Plumbing systems & piping condition",
      "Foundation & basement health",
      "Appliances — age, condition & replacement timeline",
      "Other mechanical systems",
    ],
    subsections: [
      { id:"roof",        emoji:"🏠", label:"Roof",                       hint:"Shingles, gutters, chimney, skylights, flashing, age" },
      { id:"hvac",        emoji:"🌡️",  label:"Heating / HVAC",            hint:"Furnace, boiler, AC, heat pump — label with age & model" },
      { id:"hwheater",    emoji:"💧", label:"Water Heater",               hint:"Unit label — model, serial, install date, any rust" },
      { id:"plumbing",    emoji:"🔧", label:"Plumbing & Pipes",           hint:"Under sinks, main shutoff, pipe condition, water meter" },
      { id:"foundation",  emoji:"🏗️", label:"Foundation & Basement",      hint:"Walls, floor, cracks, moisture, sump pump" },
      { id:"appliances",  emoji:"🫙", label:"Appliances",                 hint:"Refrigerator, dishwasher, washer/dryer, range — model labels" },
      { id:"mechanical",  emoji:"⚙️", label:"Other Mechanical",           hint:"Generator, irrigation, central vacuum, other systems" },
    ],
  },
  {
    key: "other_spaces",
    aiKeys: ["other","garage","exterior"],
    label: "Other Spaces",
    icon: <HomeOutlined />,
    emoji: "🏡",
    color: "#5A67D8",
    points: [
      "Garage — door, floor, mechanicals",
      "Outbuildings & sheds",
      "Pool & spa condition",
      "Yard, grading & drainage",
      "Exterior & siding condition",
      "Landscaping & tree health",
    ],
    subsections: [
      { id:"garage",       emoji:"🚗", label:"Garage",                    hint:"Door, floor, walls, overhead storage, mechanicals" },
      { id:"exterior",     emoji:"🌿", label:"Exterior & Siding",         hint:"Siding, deck/porch, driveway, walkways, grading" },
      { id:"outbuildings", emoji:"🏚️", label:"Outbuildings & Sheds",      hint:"Sheds, barns, workshops, detached structures" },
      { id:"pool",         emoji:"🏊", label:"Pool, Spa & Water Features", hint:"Equipment, pump, filter, heater, liner condition" },
      { id:"yard",         emoji:"🌳", label:"Yard & Landscaping",        hint:"Trees near house, grading, drainage, irrigation" },
    ],
  },
  {
    key: "stress_reduction",
    aiKeys: ["stress"],
    label: "Stress Reduction & Simplicity",
    icon: <SmileOutlined />,
    emoji: "🧘",
    color: "#276749",
    points: [
      "Top 5 most important priorities right now",
      "What you don't need to worry about",
      "Low-effort, high-impact improvements",
      "Maintenance automation opportunities",
      "Home system organization & simplification",
    ],
  },
  {
    key: "value_wealth",
    aiKeys: ["value","kitchen","bathrooms"],
    label: "Value, Wealth & Future Proofing",
    icon: <DollarOutlined />,
    emoji: "💰",
    color: "#744210",
    points: [
      "Kitchen updates that pay off",
      "Bathroom improvements & ROI",
      "Cosmetic upgrades — paint, flooring, fixtures",
      "Aging items to plan for",
      "Pre-sale readiness & market positioning",
      "Energy & sustainability features buyers want",
    ],
    subsections: [
      { id:"kitchen",     emoji:"🍳", label:"Kitchen",                   hint:"Appliances, cabinets, counters, sink — age & condition" },
      { id:"bathrooms",   emoji:"🚿", label:"Bathrooms",                 hint:"Fixtures, tile, caulk, ventilation, vanity" },
      { id:"cosmetics",   emoji:"🎨", label:"Cosmetics & Finishes",      hint:"Paint, flooring, trim, ceiling condition" },
      { id:"upgrades",    emoji:"✨", label:"Upgrade Opportunities",     hint:"Areas with best before/after value potential" },
      { id:"aging_items", emoji:"📅", label:"Aging Items to Plan For",   hint:"Systems or finishes nearing end of life" },
    ],
  },
  {
    key: "notes_collaboration",
    aiKeys: ["notes"],
    label: "Notes & Collaboration",
    icon: <FileTextOutlined />,
    emoji: "📝",
    color: MUTED,
    points: ["Ideas", "Feedback", "Issues", "Tasks", "Summary"],
  },
];

const AI_KEY_MAP = {
  health:      "home_health",
  energy:      "energy_efficiency",
  maintenance: "maintenance_longevity",
  stress:      "stress_reduction",
  value:       "value_wealth",
};
const CLAIM_STEPS = [
  { n:1, icon:"📍", label:"Claim Your Home",         desc:"Enter your address — pull your property details instantly." },
  { n:2, icon:"👤", label:"Create Your Free Account", desc:"Save your progress & unlock your dashboard." },
  { n:3, icon:"🩺", label:"Home Health Check-Up",     desc:"29 questions, 5 minutes — get your Health Score." },
  { n:4, icon:"🔧", label:"Build Maintenance Plan",   desc:"Set reminders so nothing falls through the cracks." },
  { n:5, icon:"📈", label:"Track Your Home's Value",  desc:"Watch your equity grow over time." },
  { n:6, icon:"🎯", label:"Set Your Home Goals",      desc:"Tell us what matters most right now." },
  { n:7, icon:"🛒", label:"Unlock Local Savings",     desc:"Geo-matched offers on products & trusted pros." },
  { n:8, icon:"🤖", label:"Ask Your Home Anything",   desc:"Your AI Home Advisor, on call 24/7." },
];
// ── HOME HEALTH CHECK — Question bank (mirrors AI site) ──────────────────
const HC_QUESTIONS = [
  { id:"smoke_co",    section:"home_health",           sLabel:"Home Health & Safety",    sIcon:"🏥", sColor:"#C53030", weight:4,
    q:"Smoke & CO Detectors", hint:"Detectors save lives. Replace every 7-10 years and test monthly.",
    inputLabel:"How old are your smoke/CO detectors?",
    opts:[ {v:4,l:"Tested recently, under 5 years old"}, {v:3,l:"Installed, 5-7 years old"}, {v:2,l:"Installed but not recently tested"}, {v:1,l:"Some missing or over 10 years old"}, {v:0,l:"Not sure / none installed"}, {v:0,l:"Unknown"} ] },
  { id:"radon",       section:"home_health",           sLabel:"Home Health & Safety",    sIcon:"🏥", sColor:"#C53030", weight:4,
    q:"Radon Testing", hint:"Radon is the #2 cause of lung cancer. Test kits cost under $30.",
    inputLabel:"When was your home last tested for radon?",
    opts:[ {v:4,l:"Tested in last 2 years — levels safe"}, {v:3,l:"Tested over 2 years ago — safe"}, {v:2,l:"Tested — borderline results"}, {v:1,l:"Never tested"}, {v:0,l:"Known elevated levels, no mitigation"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"watersource",  section:"home_health",          sLabel:"Home Health & Safety",    sIcon:"🏥", sColor:"#C53030", weight:4,
    q:"Water Quality & Source", hint:"Well water should be tested annually. Municipal tap water can still have lead from older pipes.",
    inputLabel:"When was your water last tested?",
    opts:[ {v:4,l:"Municipal — recent test, all clear"}, {v:3,l:"Well water tested within last year"}, {v:2,l:"Municipal — never tested at tap"}, {v:1,l:"Well water, tested over 2 years ago"}, {v:0,l:"Known issues or never tested"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"electric",    section:"home_health",           sLabel:"Home Health & Safety",    sIcon:"🏥", sColor:"#C53030", weight:4,
    q:"Electrical Panel & Wiring", hint:"Federal Pacific and Zinsco panels are known fire hazards. Grounded outlets are required for safety.",
    inputLabel:"How old is your electrical panel?",
    opts:[ {v:4,l:"Modern 200-amp panel, updated within 20 yrs"}, {v:3,l:"Panel 20-40 years old, no known issues"}, {v:2,l:"Fuse box or panel over 40 years old"}, {v:1,l:"Known Federal Pacific/Zinsco panel"}, {v:0,l:"Knob-and-tube or aluminum wiring"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"concerns",    section:"home_health",           sLabel:"Home Health & Safety",    sIcon:"🏥", sColor:"#C53030", weight:4,
    q:"Known Health & Safety Issues", hint:"Mold, asbestos, lead paint, pest damage — early detection saves money and health.",
    inputLabel:"Any known health or safety issues in your home?",
    opts:[ {v:4,l:"No known issues — clean history"}, {v:3,l:"Minor issues, addressed and resolved"}, {v:2,l:"Minor suspected issues, not investigated"}, {v:1,l:"Known issues not yet addressed"}, {v:0,l:"Major issues (mold, asbestos, lead, structural)"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"attic",       section:"energy_efficiency",     sLabel:"Energy & Utility Savings", sIcon:"⚡", sColor:"#D97706", weight:4,
    q:"Attic Insulation", hint:"Attic insulation is the single highest-ROI energy upgrade. R-38 to R-60 recommended.",
    inputLabel:"Do you know your attic insulation R-value?",
    opts:[ {v:4,l:"Well insulated — R-38 or higher"}, {v:3,l:"Insulated — R-19 to R-37"}, {v:2,l:"Some insulation — inadequate or aging"}, {v:1,l:"Minimal or no attic insulation"}, {v:0,l:"No insulation — known heat/cold loss"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"windows",     section:"energy_efficiency",     sLabel:"Energy & Utility Savings", sIcon:"⚡", sColor:"#D97706", weight:4,
    q:"Windows & Doors", hint:"Windows and doors account for 25-30% of heating and cooling losses.",
    inputLabel:"Approximate age of your windows and doors?",
    opts:[ {v:4,l:"Double/triple pane — under 15 years old"}, {v:3,l:"Double pane — 15-25 years, minor drafts"}, {v:2,l:"Single pane or over 25 years old"}, {v:1,l:"Noticeable drafts and condensation"}, {v:0,l:"Original windows — single pane, leaking"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"air_sealing", section:"energy_efficiency",     sLabel:"Energy & Utility Savings", sIcon:"⚡", sColor:"#D97706", weight:4,
    q:"Air Sealing & Drafts", hint:"Air leaks account for 25-40% of heating/cooling costs. Simple fixes save hundreds per year.",
    inputLabel:"Noticeable drafts near outlets, doors, or basement?",
    opts:[ {v:4,l:"Well sealed — professionally tested"}, {v:3,l:"No noticeable drafts, never tested"}, {v:2,l:"Minor drafts near windows or doors"}, {v:1,l:"Noticeable drafts — cold spots in winter"}, {v:0,l:"Major air leaks throughout"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"solar",       section:"energy_efficiency",     sLabel:"Energy & Utility Savings", sIcon:"⚡", sColor:"#D97706", weight:4,
    q:"Solar & Renewable Energy", hint:"Solar can eliminate electric bills and increase home value.",
    inputLabel:"Do you have solar panels or have you evaluated them?",
    opts:[ {v:4,l:"Solar installed — net metering active"}, {v:3,l:"Evaluated — installation scheduled"}, {v:2,l:"Interested — not yet evaluated"}, {v:1,l:"Not suitable or not interested"}, {v:0,l:"Have not thought about it"}, {v:0,l:"Unknown"} ] },
  { id:"roof",        section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Roof Condition & Age", hint:"An aging roof is your #1 risk for major unexpected expense. Average asphalt roof lasts 20-30 years.",
    inputLabel:"How old is your roof? When was it last inspected?",
    opts:[ {v:4,l:"Under 10 years — excellent condition"}, {v:3,l:"10-20 years — good, minor wear"}, {v:2,l:"20-25 years or missing shingles"}, {v:1,l:"25+ years or active leak"}, {v:0,l:"Active leak or structural damage"}, {v:0,l:"Unknown age"} ] },
  { id:"hvac",        section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Heating & Cooling (HVAC)", hint:"Annual HVAC service extends life and maintains efficiency by up to 20%. Systems last 15-25 years.",
    inputLabel:"How old is your HVAC? Last service date?",
    opts:[ {v:4,l:"Under 10 yrs — serviced annually"}, {v:3,l:"10-15 yrs — serviced within 2 years"}, {v:2,l:"15-20 yrs or not serviced in 3+ years"}, {v:1,l:"Over 20 years or known issues"}, {v:0,l:"Not functioning or unknown history"}, {v:0,l:"Unknown age / no records"} ] },
  { id:"hwheater",    section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Water Heater", hint:"Traditional water heaters last 8-12 years. A failing unit in a finished area causes significant damage.",
    inputLabel:"How old is your water heater? Any issues?",
    opts:[ {v:4,l:"Under 7 years — no issues"}, {v:3,l:"7-10 years — functioning well"}, {v:2,l:"10-13 years or occasional issues"}, {v:1,l:"Over 13 years — traditional tank"}, {v:0,l:"Leaking, rust, or rumbling sounds"}, {v:0,l:"Unknown age"} ] },
  { id:"plumbing",    section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Plumbing & Pipes", hint:"Pipe material matters. Polybutylene (grey flexible pipes) are a known failure risk.",
    inputLabel:"Do you know your pipe material and age?",
    opts:[ {v:4,l:"Copper or PEX — no known leaks"}, {v:3,l:"Mix of materials — minor issues resolved"}, {v:2,l:"Older galvanized or unknown material"}, {v:1,l:"Polybutylene pipes (grey, flexible)"}, {v:0,l:"Active leaks or discolored water"}, {v:0,l:"Unknown / no records"} ] },
  { id:"foundation",  section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Foundation & Basement", hint:"Foundation issues are the most expensive home repair. Early detection saves tens of thousands.",
    inputLabel:"Any cracks, settling, or water intrusion?",
    opts:[ {v:4,l:"Solid — dry, no cracks"}, {v:3,l:"Minor hairline cracks — no movement"}, {v:2,l:"Efflorescence or minor moisture"}, {v:1,l:"Visible cracks with movement or moisture"}, {v:0,l:"Active water intrusion or bowing walls"}, {v:0,l:"Unknown / not inspected"} ] },
  { id:"appliances",  section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Major Appliances", hint:"Knowing appliance ages helps you plan and budget proactively before they fail.",
    inputLabel:"Average age of major appliances?",
    opts:[ {v:4,l:"All under 7 years — running well"}, {v:3,l:"Mix of ages — mostly under 12 years"}, {v:2,l:"Most 12-18 years old"}, {v:1,l:"Several over 18 or recent breakdowns"}, {v:0,l:"Multiple failing units"}, {v:0,l:"Unknown ages"} ] },
  { id:"exterior",    section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Exterior, Siding & Paint", hint:"Exterior maintenance prevents moisture infiltration — one of the most common causes of structural damage.",
    inputLabel:"Condition and age of exterior siding and paint?",
    opts:[ {v:4,l:"Excellent — painted/sided within 7 years"}, {v:3,l:"Good — minor wear, touch-up needed"}, {v:2,l:"Paint peeling or siding showing wear"}, {v:1,l:"Significant deterioration or rotting wood"}, {v:0,l:"Major siding failure or moisture infiltration"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"garage",      section:"maintenance_longevity", sLabel:"Maintenance & Longevity",  sIcon:"🔧", sColor:"#2D6A4F", weight:4,
    q:"Garage & Mechanical", hint:"Garage door auto-reverse is a safety requirement. CO alarms in attached garages save lives.",
    inputLabel:"Age and condition of garage door and opener?",
    opts:[ {v:4,l:"Insulated door, modern opener, CO alarm"}, {v:3,l:"Functioning door and opener — no CO alarm"}, {v:2,l:"Old opener (no auto-reverse)"}, {v:1,l:"Manual door only"}, {v:0,l:"Damaged door or CO concerns"}, {v:0,l:"No garage / not applicable"} ] },
  { id:"pool",        section:"other_spaces",          sLabel:"Other Spaces",             sIcon:"🌳", sColor:"#276749", weight:4,
    q:"Pool, Spa & Water Features", hint:"A well-maintained pool adds value; a neglected one creates liability.",
    inputLabel:"Do you have a pool/spa? Last service date?",
    opts:[ {v:4,l:"No pool — no additional maintenance"}, {v:4,l:"Pool professionally serviced — up to code"}, {v:2,l:"Pool present — serviced occasionally"}, {v:1,l:"Pool present — infrequently serviced"}, {v:0,l:"Pool in poor condition, no safety barriers"}, {v:0,l:"Unknown / not applicable"} ] },
  { id:"outbuildings",section:"other_spaces",          sLabel:"Other Spaces",             sIcon:"🌳", sColor:"#276749", weight:4,
    q:"Outbuildings, Sheds & Fences", hint:"Maintained outbuildings add value; neglected ones create liabilities.",
    inputLabel:"Condition of sheds, fences, and outbuildings?",
    opts:[ {v:4,l:"No outbuildings or all excellent"}, {v:3,l:"Good — minor cosmetic maintenance"}, {v:2,l:"Moderate wear — some repairs needed"}, {v:1,l:"Poor — significant repair needed"}, {v:0,l:"Unsafe structures"}, {v:0,l:"Unknown / not applicable"} ] },
  { id:"yard",        section:"other_spaces",          sLabel:"Other Spaces",             sIcon:"🌳", sColor:"#276749", weight:4,
    q:"Yard, Landscaping & Drainage", hint:"Proper grading protects your foundation. Trees near the house can cause roof and foundation damage.",
    inputLabel:"Drainage issues, grading, tree proximity?",
    opts:[ {v:4,l:"Well graded — no pooling, trees maintained"}, {v:3,l:"Minor grading issues, trees could be trimmed"}, {v:2,l:"Some pooling near foundation after rain"}, {v:1,l:"Trees close to house or regular pooling"}, {v:0,l:"Water pools against foundation"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"smart_home",  section:"stress_reduction",      sLabel:"Stress & Simplicity",      sIcon:"🧘", sColor:"#553C9A", weight:4,
    q:"Smart Home & Monitoring", hint:"Smart thermostats, leak sensors, and cameras reduce stress and save money.",
    inputLabel:"What smart home technology do you have?",
    opts:[ {v:4,l:"Thermostat, leak sensors, security, doorbell"}, {v:3,l:"Smart thermostat and basic security"}, {v:2,l:"Basic security only"}, {v:1,l:"Traditional thermostat — no monitoring"}, {v:0,l:"No smart technology or security"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"emergency_prep",section:"stress_reduction",    sLabel:"Stress & Simplicity",      sIcon:"🧘", sColor:"#553C9A", weight:4,
    q:"Emergency Preparedness", hint:"Knowing your shut-offs and having a basic plan can save your home in an emergency.",
    inputLabel:"Do you know your water, gas, and electric shut-offs?",
    opts:[ {v:4,l:"Know all shut-offs — plan in place"}, {v:3,l:"Know shut-offs — no formal plan"}, {v:2,l:"Know water shut-off but unsure of others"}, {v:1,l:"Not sure where shut-offs are"}, {v:0,l:"No emergency preparedness"}, {v:0,l:"Unknown / not applicable"} ] },
  { id:"cosmetics",   section:"stress_reduction",      sLabel:"Stress & Simplicity",      sIcon:"🧘", sColor:"#553C9A", weight:4,
    q:"Interior Condition", hint:"A well-maintained interior reduces daily stress and signals overall home care culture.",
    inputLabel:"Overall interior condition — paint, floors, fixtures?",
    opts:[ {v:4,l:"Excellent — updated within 5 years"}, {v:3,l:"Good — normal wear, cosmetically dated"}, {v:2,l:"Fair — peeling paint, worn floors"}, {v:1,l:"Poor — significant deferred maintenance"}, {v:0,l:"Very poor — affecting daily comfort"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"upgrades",    section:"value_wealth",          sLabel:"Value & Future Proofing",  sIcon:"💰", sColor:"#744210", weight:4,
    q:"Recent Upgrades & Investments", hint:"Strategic upgrades return 60-150% at resale. Kitchen and bathrooms return the most.",
    inputLabel:"What major upgrades have you made in the last 5 years?",
    opts:[ {v:4,l:"Major upgrades — kitchen, baths, or systems"}, {v:3,l:"Some upgrades — cosmetic or systems"}, {v:2,l:"Minor updates only"}, {v:1,l:"No upgrades — original as purchased"}, {v:0,l:"Deferred maintenance has accumulated"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"aging_items", section:"value_wealth",          sLabel:"Value & Future Proofing",  sIcon:"💰", sColor:"#744210", weight:4,
    q:"Aging Systems — Budget Planning", hint:"Knowing what is aging allows proactive budgeting. Surprises are the most expensive home costs.",
    inputLabel:"Do you know the ages of your major systems?",
    opts:[ {v:4,l:"Know all ages — budget set aside"}, {v:3,l:"Know most ages — informal awareness"}, {v:2,l:"Know some ages — no budget"}, {v:1,l:"Unsure of ages — no planning"}, {v:0,l:"No awareness of system ages"}, {v:0,l:"Unknown / no records"} ] },
  { id:"permits",     section:"value_wealth",          sLabel:"Value & Future Proofing",  sIcon:"💰", sColor:"#744210", weight:4,
    q:"Permits, Records & Documentation", hint:"Unpermitted work can block sales, void insurance, and create liability.",
    inputLabel:"Do you have records of improvements and permits?",
    opts:[ {v:4,l:"Full records — all work permitted"}, {v:3,l:"Most records kept — some gaps"}, {v:2,l:"Partial records — major work documented"}, {v:1,l:"Few records — some work may be unpermitted"}, {v:0,l:"No records — unsure of permit status"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"insurance",   section:"value_wealth",          sLabel:"Value & Future Proofing",  sIcon:"💰", sColor:"#744210", weight:4,
    q:"Home Insurance Review", hint:"Most homeowners are underinsured. Rebuild costs rise with inflation — your coverage should match.",
    inputLabel:"When did you last review your homeowners insurance?",
    opts:[ {v:4,l:"Reviewed within last year — coverage verified"}, {v:3,l:"Reviewed 1-3 years ago"}, {v:2,l:"Original policy — never reviewed"}, {v:1,l:"Never reviewed — unsure of coverage"}, {v:0,l:"Coverage lapsed or significantly below value"}, {v:0,l:"Unknown / not sure"} ] },
  { id:"help_now",    section:"notes_collaboration",   sLabel:"Your Home Goals",          sIcon:"📝", sColor:"#1A2280", weight:2,
    q:"What is your most pressing home concern right now?", hint:"This helps us connect you with the right professional or resource.",
    inputLabel:"Tell us what is on your mind:",
    opts:[ {v:2,l:"I have a specific issue needing help"}, {v:2,l:"I would like to understand my options"}, {v:1,l:"Just exploring — not urgent"}, {v:1,l:"No concerns right now"}, {v:0,l:"Not sure / prefer not to say"} ] },
  { id:"goals",       section:"notes_collaboration",   sLabel:"Your Home Goals",          sIcon:"📝", sColor:"#1A2280", weight:2,
    q:"What is your primary goal for your home?", hint:"Knowing your goal helps us give you the most relevant advice and resources.",
    inputLabel:"Any specific goals or timelines:",
    opts:[ {v:2,l:"Sell within 12 months — maximize value"}, {v:2,l:"Stay and improve — safer and more comfortable"}, {v:2,l:"Refinance or HELOC — maximize appraised value"}, {v:2,l:"Just moved in — understand what I have"}, {v:2,l:"Long term hold — maintaining and tracking"}, {v:0,l:"Not sure yet"} ] },
];

const HC_MAX_RAW = HC_QUESTIONS.reduce((t, q) => t + q.weight, 0);
const HC_SECTION_KEYS = [...new Set(HC_QUESTIONS.map(q => q.section))];

function hcCalcPortalScores(answers) {
  let total = 0;
  const bySec = {};
  HC_SECTION_KEYS.forEach(k => { bySec[k] = { earned: 0, max: 0, label: '', icon: '', color: '' }; });
  HC_QUESTIONS.forEach(q => {
    bySec[q.section].max   += q.weight;
    bySec[q.section].label  = q.sLabel;
    bySec[q.section].icon   = q.sIcon;
    bySec[q.section].color  = q.sColor;
    const ans = answers[q.id];
    if (ans) { total += (ans.v || 0); bySec[q.section].earned += (ans.v || 0); }
  });
  const pct = Math.min(90, Math.round((total / HC_MAX_RAW) * 90));
  Object.keys(bySec).forEach(k => {
    bySec[k].pct = Math.min(90, Math.round((bySec[k].earned / bySec[k].max) * 90));
  });
  return { total, maxRaw: HC_MAX_RAW, pct, bySec };
}

function hcBuddhaLevel(pct) {
  if (pct >= 81) return { label: 'Enlightened', emoji: '🧘', color: '#D4921A' };
  if (pct >= 66) return { label: 'Happy',       emoji: '😊', color: '#38A169' };
  if (pct >= 50) return { label: 'Content',     emoji: '🙂', color: '#3182CE' };
  if (pct >= 33) return { label: 'Concerned',   emoji: '😐', color: '#D97706' };
  return              { label: 'Worried',        emoji: '😟', color: '#C53030' };
}


class HomeBuddha extends Component {
  constructor(props) {
    super(props);
    const initialSection = props.initialSection && SECTIONS.some(s => s.key === props.initialSection) ? props.initialSection : "overview";
    const initialOpenSubsections = props.initialSection && props.initialSubId
      ? { [props.initialSection + ":" + props.initialSubId]: true }
      : {};
    this.state = {
      activeKey:       initialSection,
      loading:         true,
      aiReport:        null,
      manualEntries:   {},
      chatMessages:    {},
      chatInput:       {},
      chatLoading:     {},
      openSubsections: initialOpenSubsections,
      subChatMessages: {},
      subChatInput:    {},
      subChatLoading:  {},
      subAnalyzing:    {},
      subAnalysis:     {},
      homes:           [],
      aiReports:       [],
      activeHomeId:    null,
      setupFor:        "self",
      clientEmail:     "",
      clientName:      "",
      vtPurchasePrice:   "", vtPurchaseDate:    "",
      vtMortgageBalance: "", vtMortgageRate:    "",
      vtMortgageType:    "30yr fixed",
      vtDownPayment:     "", vtPropertyTax:     "",
      vtExpanded:        false,
      referringAgent:  null,
      agentChatOpen:   false,
      agentChatMessages: [],
      agentChatInput:  "",
      agentChatLoading: false,
      setupStep:       0,
      setupMode:       null,
      setupAddress:    "",
      setupPublicData: null,
      setupEdits:      {},
      setupLoading:    false,
      savedReports:    [],
      selectedReport:  null,
      reportsExpanded: false,
      myClients:       [],
      showInviteModal: false,
      inviteEmail:     "",
      inviteName:      "",
      inviteSending:   false,
      helpModal:       null,
      helpType:        "",
      helpNote:        "",
      helpSubmitting:  false,
      // ── Health Check Quiz state ─────────────────────────────────────
      hcOpen:          false,
      hcStep:          -1,
      hcAnswers:       {},
      hcScore:         null,
      hcSaving:        false,
      hcFlagged:        [],
      hcPriorityChoices: {},
      hcSummaryText:    "",
      hcSummaryLoading: false,
      hcActionsSaving:  false,
      // ── Reminder modal state ────────────────────────────────────────
      reminderModal:   null,
      reminderDate:    "",
      reminderTime:    "09:00",
      reminderRepeat:  "none",
      reminderNote:    "",
      reminderSaving:  false,
      reminderSaving:  false,
      manualStepOverrides: {},
    };
  }

  componentDidMount() {
    this.loadAll().then(() => { this.loadSavedReports(); this.loadStepOverrides(); });
    this.loadMyClients();
  }

  loadMyClients = async () => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId;
      if (!userId || (ud.userProfile !== "AG" && ud.userProfile !== "L")) return;
      const resp = await noAuthGetInfo(`getUsers?referredBy=${userId}`);
      if (resp?.success && Array.isArray(resp?.data) && resp.data.length > 0) {
        const clients = resp.data.filter(u =>
          u.userProfile === "B" || u.userProfile === "S" || u.userProfile === "HO"
        );
        this.setState({ myClients: clients });
        return;
      }
    } catch(e) { console.warn("loadMyClients referredBy query:", e.message); }
    try {
      const homes2 = this.state.homes || [];
      const coUserIds = [];
      homes2.forEach(h => {
        (h.coUsers || []).forEach(cu => {
          if (cu.userId && !coUserIds.includes(cu.userId)) coUserIds.push(cu.userId);
        });
      });
      if (coUserIds.length > 0) {
        const fetched = await Promise.all(
          coUserIds.map(id => noAuthgetDetails("getUsers", id).catch(() => null))
        );
        const clients = fetched
          .filter(r => r?.success && r?.data)
          .map(r => r.data)
          .filter(u => u.userProfile === "B" || u.userProfile === "S" || u.userProfile === "HO");
        if (clients.length > 0) this.setState({ myClients: clients });
      }
    } catch(e) { console.warn("loadMyClients coUsers fallback:", e.message); }
  };

  loadSavedReports = async () => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId || ud.userid;
      if (!userId) return;
      const resp = await list("mybuddha", { userId });
      const directReports = resp?.success ? (resp.data || []).filter(r => r.address) : [];
      const allHomeProfiles = [
        ...(resp?.data || []),
        ...(this.state.homes || []),
      ].filter((r, i, arr) =>
        (r.type === "home_profile" || r.action === "home_profile") &&
        arr.findIndex(x => x.id === r.id) === i
      );
      const linkedReports = [];
      allHomeProfiles.forEach(home => {
        if (home.linkedReportData) {
          const lr = home.linkedReportData;
          linkedReports.push({
            id:         lr.id || home.id + "_linked",
            address:    lr.address || home.address || home.nickname || "",
            reportType: lr.reportType || "buyer",
            recordType: lr.reportType || "buyer",
            updatedAt:  lr.updatedAt || home.createdAt || 0,
            createdAt:  lr.updatedAt || home.createdAt || 0,
            isLinked:   true,
            agentId:    home.agentId || "",
          });
        }
      });
      const allReports = [...directReports, ...linkedReports];
      const seen = {};
      allReports.forEach(r => {
        const key = (r.address || "").toLowerCase().trim() + "|" + (r.reportType || r.recordType || "any");
        const ts  = r.updatedAt || r.createdAt || 0;
        if (!seen[key] || ts > (seen[key].updatedAt || 0)) seen[key] = r;
      });
      const reports = Object.values(seen).sort((a, b) =>
        (b.updatedAt || b.createdAt || 0) - (a.updatedAt || a.createdAt || 0)
      );
      this.setState({ savedReports: reports });
    } catch (err) {
      console.error("loadSavedReports:", err);
    }
  };

  fetchPublicRecords = async (address) => {
    this.setState({ setupLoading: true, setupPublicData: null });
    try {
      const resp = await fetch("https://ai.myhomebuddha.com/.netlify/functions/property-data", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address }),
      });
      let publicData = { address };
      if (resp.ok) {
        const data = await resp.json();
        const s = data.subject || {};
        const p = data.propData || {};
        const avmVal = data.avm ? String(data.avm.price || data.avm.value || "") : "";
        publicData = {
          address,
          yearBuilt:      String(s.yearBuilt      || p.yearBuilt      || ""),
          squareFeet:     String(s.sqft            || p.squareFootage  || p.livingArea || ""),
          bedrooms:       String(s.beds            || p.bedrooms       || ""),
          bathrooms:      String(s.baths           || p.bathrooms      || ""),
          lotSize:        String(p.lotSize         || p.lot_size       || ""),
          propertyType:   String(s.propertyType    || p.propertyType   || p.type || ""),
          stories:        String(p.stories         || ""),
          garageSpaces:   String(p.garageSpaces    || p.garage         || ""),
          heatingType:    String(p.heating         || p.heatingType    || ""),
          coolingType:    String(p.cooling         || p.coolingType    || ""),
          roofType:       String(p.roofType        || p.roof           || ""),
          foundationType: String(p.foundationType  || p.foundation     || ""),
          lastSoldYear:   p.lastSaleDate ? new Date(p.lastSaleDate).getFullYear().toString() : "",
          estimatedValue: avmVal,
          county:         String(p.county          || ""),
          state:          String(p.state           || p.stateCode      || ""),
          zipCode:        String(p.zipCode         || p.zip            || p.postalCode || ""),
        };
        if (!data.found || !data.subject) {
          notification.info({ message: "Partial property data", description: "Some fields couldn't be found automatically — please fill them in.", duration: 4 });
        }
      }
      this.setState({ setupPublicData: publicData, setupLoading: false, setupEdits: {}, setupStep: 2 });
    } catch (err) {
      console.error("fetchPublicRecords:", err);
      this.setState({ setupPublicData: { address }, setupLoading: false, setupEdits: {}, setupStep: 2 });
      notification.info({ message: "Couldn't auto-fill records", description: "Please enter your home details below.", duration: 3 });
    }
  };

  saveAndAnalyze = async () => {
    const { setupPublicData, setupEdits, setupAddress, selectedReport } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId || ud.userid;
    if (!userId) return;
    const homeDetails = { ...(setupPublicData || {}), ...setupEdits };
    const address = homeDetails.address || setupAddress || selectedReport?.address || "";
    this.setState({ setupLoading: true });
    const { setupFor, clientEmail, clientName, vtPurchasePrice, vtPurchaseDate, vtMortgageBalance, vtMortgageRate, vtMortgageType, vtDownPayment, vtPropertyTax } = this.state;
    const valueTrackerPrefill = (vtPurchasePrice || vtMortgageBalance) ? {
      purchasePrice:   vtPurchasePrice,
      purchaseDate:    vtPurchaseDate,
      mortgageBalance: vtMortgageBalance,
      mortgageRate:    vtMortgageRate,
      mortgageType:    vtMortgageType || "30yr fixed",
      downPayment:     vtDownPayment,
      propertyTax:     vtPropertyTax,
    } : null;
    try {
      const coUsers = setupFor === "client" && clientEmail ? [{ email: clientEmail, name: clientName || clientEmail.split("@")[0], role: "owner", addedAt: Date.now() }] : [];
      let targetUserId = userId;
      let clientUserId = null;
      if (setupFor === "client" && clientEmail) {
        try {
          const cl = await noAuthGetInfo("checkUser/" + encodeURIComponent(clientEmail));
          if (cl?.success && cl?.data?.[0]?.id) clientUserId = cl.data[0].id;
        } catch(e) { console.warn("checkUser lookup:", e.message); }
        if (!clientUserId) {
          try {
            const cl2 = await noAuthGetInfo("getUsers?email=" + encodeURIComponent(clientEmail));
            if (cl2?.success && cl2?.data?.[0]?.id) clientUserId = cl2.data[0].id;
          } catch(e) { console.warn("email search lookup:", e.message); }
        }
        if (clientUserId) targetUserId = clientUserId;
      }
      const homeRecord = await add("mybuddha", {
        entityData: {
          userId: targetUserId,
          agentId: setupFor === "client" ? userId : null,
          type: "home_profile", action: "home_profile", address,
          nickname: address.split(",")[0] || (setupFor === "client" ? (clientName + "'s Home") : "My Home"),
          homeDetails, coUsers,
          setupSource: selectedReport ? "saved_report" : "manual",
          linkedReportId: selectedReport?.id || null,
          linkedReportData: selectedReport ? { id: selectedReport.id, address: selectedReport.address || address, reportType: selectedReport.reportType || "buyer", updatedAt: selectedReport.updatedAt || Date.now() } : null,
          valueTrackerPrefill,
          createdBy: userId, createdAt: Date.now(),
        },
      });
      if (setupFor === "client" && clientEmail) {
        try {
          const ud2 = JSON.parse(localStorage.getItem("userDetails") || "{}");
          const agName2 = `${ud2.firstName || ""} ${ud2.lastName || ""}`.trim();
          const agBrand2 = JSON.parse(localStorage.getItem("mhb_agent_brand") || "{}");
          const hasReport = !!selectedReport;
          const reports = hasReport && selectedReport ? [{
            reportType: (selectedReport.reportType || selectedReport.recordType || "AI Home Analysis").replace(/_/g, " ").replace(/\w/g, c => c.toUpperCase()),
            date: selectedReport.updatedAt ? new Date(selectedReport.updatedAt).toLocaleDateString() : new Date().toLocaleDateString(),
          }] : [];
          await add("sendInviteEmail", {
            entityData: {
              toEmail:      clientEmail,
              toName:       clientName || clientEmail.split("@")[0],
              fromName:     agName2,
              fromEmail:    ud2.email || "",
              agentId:      ud2.id || ud2.userId || "",
              agentPhone:   ud2.phone || ud2.phno || "",
              agentPhoto:   agBrand2.photo || "",
              agentCompany: agBrand2.company || agBrand2.brokerage || ud2.newdata?.companyName || "",
              address,
              homeId:       homeRecord?.data?.id || "",
              templateId:   hasReport ? "d-5129af67a82e4e43bade36236d5340dd" : "d-c5bad335ea5f46f8865b9de868d19045",
              reports,
            },
          });
          notification.success({ message: `Invite sent to ${clientEmail}` });
        } catch (inviteErr) {
          notification.info({ message: "Home saved — invite email will be sent shortly" });
        }
      }
      localStorage.removeItem("mhb_pending_home");
      notification.success({ message: "Home profile saved! Opening your portal..." });
      await this.loadAll();
      await this.loadSavedReports();
      if (this.props.onCloseSetupModal) this.props.onCloseSetupModal();
      this.setState({ setupStep: 0, setupMode: null, setupAddress: "", setupPublicData: null, setupEdits: {}, selectedReport: null, setupLoading: false, setupFor: "self", clientEmail: "", clientName: "", activeKey: "home_health", vtPurchasePrice: "", vtPurchaseDate: "", vtMortgageBalance: "", vtMortgageRate: "", vtMortgageType: "30yr fixed", vtDownPayment: "", vtPropertyTax: "", vtExpanded: false });
    } catch (err) {
      console.error("saveAndAnalyze:", err);
      notification.error({ message: "Error saving — please try again" });
      this.setState({ setupLoading: false });
    }
  };


  // ── loadAll with secondary HC query for referred clients ─────────
  loadAll = async () => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId || ud.userid;
      const userEmail = ud.email || "";
      if (!userId) { this.setState({ loading: false }); return; }

      const resp = await list("mybuddha", { userId });
      if (!resp?.success || !Array.isArray(resp.data)) { this.setState({ loading: false }); return; }

      let coUserRecords = [];
      let refId = ud.referedById || ud.referredBy || ud.invitedBy || ud.referredByAgent;
      if (!refId && userId) {
        try {
          const freshUser = await noAuthgetDetails("getUsers", userId);
          if (freshUser?.success && freshUser?.data) {
            const fu = freshUser.data;
            refId = fu.referedById || fu.referredBy || fu.invitedBy || fu.referredByAgent;
            if (refId) console.log("[HomeBuddha] got refId from fresh DB fetch:", refId);
          }
        } catch(e) { console.warn("fresh user fetch for refId:", e.message); }
      }

      if (refId && userEmail) {
        try {
          const agentResp = await list("mybuddha", { userId: refId });
          if (agentResp?.success && Array.isArray(agentResp.data)) {
            const clientEmail = userEmail.toLowerCase();
            coUserRecords = agentResp.data.filter(r => {
              if (!r.coUsers || !Array.isArray(r.coUsers)) return false;
              return r.coUsers.some(cu => cu.email?.toLowerCase() === clientEmail);
            });
          }
        } catch(e) { console.warn("agent coUser query:", e.message); }
      }

      if (coUserRecords.length === 0 && userEmail) {
        try {
          const freshUd = JSON.parse(localStorage.getItem("userDetails") || "{}");
          const freshRefId = freshUd.referedById || freshUd.referredBy || freshUd.invitedBy || freshUd.referredByAgent;
          if (freshRefId && freshRefId !== refId) {
            const freshResp = await list("mybuddha", { userId: freshRefId });
            if (freshResp?.success && Array.isArray(freshResp.data)) {
              const clientEmail = userEmail.toLowerCase();
              const freshMatches = freshResp.data.filter(r =>
                Array.isArray(r.coUsers) && r.coUsers.some(cu => cu.email?.toLowerCase() === clientEmail)
              );
              coUserRecords = [...coUserRecords, ...freshMatches];
            }
          }
        } catch(e) { console.warn("fresh refId scan:", e.message); }
      }

      const seenIds = new Set(resp.data.map(r => r.id));
      coUserRecords.forEach(r => {
        if (!seenIds.has(r.id)) { seenIds.add(r.id); resp.data.push(r); }
      });

      let aiReport = null;
      const manualEntries = {};
      let hcScoreFromDB = null;
      const portalHomes = [], aiReports = [];

      // ── forEach: build portalHomes, aiReports, manualEntries ──────────────
      resp.data.forEach(entry => {
        const sk = entry.sectionKey || entry.section || "";

        if (entry.type === "health_check") {
          if (entry.hcScore) {
            hcScoreFromDB = { pct: entry.hcScore, total: entry.hcRaw || 0, maxRaw: entry.hcMaxRaw || 0,
              bySec: entry.hcSectionScores ? JSON.parse(entry.hcSectionScores) : {} };
          }
          if (entry.hcAnswers) {
            try {
              const answers = JSON.parse(entry.hcAnswers);
              HC_QUESTIONS.forEach(q => {
                const ans = answers[q.id];
                if (!ans || ans.skipped) return;
                const qsk = q.section;
                if (!manualEntries[qsk]) manualEntries[qsk] = [];
                const exists = manualEntries[qsk].some(e => e.hcQuestionId === q.id);
                if (!exists) {
                  manualEntries[qsk].push({
                    id:           "hc_" + q.id + "_" + (entry.createdAt || Date.now()),
                    hcQuestionId: q.id,
                    description:  q.q + ": " + (ans.l || ans.label || ""),
                    notes:        ans.input || "",
                    hcScore:      ans.v !== undefined ? ans.v : (ans.val || 0),
                    hcMax:        q.weight,
                    source:       "health_check",
                    createdAt:    entry.createdAt,
                  });
                }
              });
            } catch(e) { console.warn("[HC] failed to parse hcAnswers:", e); }
          }
          return;
        }

        if (entry.type === "health_check_entry" && sk) {
          if (!manualEntries[sk]) manualEntries[sk] = [];
          manualEntries[sk].push({
            id:           entry.id,
            hcQuestionId: entry.hcQuestionId,
            description:  entry.description,
            notes:        entry.notes || "",
            hcScore:      entry.hcScore,
            hcMax:        entry.hcMax,
            source:       "health_check",
            createdAt:    entry.createdAt,
          });
          return;
        }

        if (entry.address) {
          if (sk === "home_setup" || entry.type === "home_profile" || entry.action === "home_profile") {
            portalHomes.push(entry);
            if (!aiReport || (entry.updatedAt||0) > (aiReport.updatedAt||0)) aiReport = entry;
          } else { aiReports.push(entry); }
          return;
        }

        if (!manualEntries[sk]) manualEntries[sk] = [];
        manualEntries[sk].push({ id: entry.id, description: entry.description, notes: entry.notes, brand: entry.brand, model: entry.model, year: entry.year, rating: entry.rating, analysisResult: entry.analysisResult || "", subsectionId: entry.subsectionId, photos: (entry.photos || []).map((key, i) => ({ uid: `-${i+1}`, name: key || `photo${i+1}`, status: "done", url: (entry.photoUrls || [])[i] })) });
      });
      // ── End forEach ───────────────────────────────────────────────────────

      // ── Secondary HC query for referred clients ───────────────────────────
      if (portalHomes.length > 0) {
        for (const home of portalHomes) {
          const homeAgentId = home.agentId || home.createdBy;
          if (!homeAgentId || homeAgentId === userId) continue;
          const alreadyHasHC = Object.values(manualEntries).some(arr =>
            Array.isArray(arr) && arr.some(e => e.source === "health_check")
          );
          if (alreadyHasHC) break;
          try {
            const agentResp2 = await list("mybuddha", { userId: homeAgentId });
            if (agentResp2?.success && Array.isArray(agentResp2.data)) {
              agentResp2.data.forEach(entry => {
                const esk = entry.sectionKey || entry.section || "";
                if (entry.homeId !== home.id && entry.address !== home.address) return;

                if (entry.type === "health_check" && !hcScoreFromDB) {
                  if (entry.hcScore) {
                    hcScoreFromDB = { pct: entry.hcScore, total: entry.hcRaw || 0,
                      maxRaw: entry.hcMaxRaw || 0,
                      bySec: entry.hcSectionScores ? JSON.parse(entry.hcSectionScores) : {} };
                  }
                  if (entry.hcAnswers) {
                    try {
                      const answers = JSON.parse(entry.hcAnswers);
                      HC_QUESTIONS.forEach(q => {
                        const ans = answers[q.id];
                        if (!ans || ans.skipped) return;
                        const qsk = q.section;
                        if (!manualEntries[qsk]) manualEntries[qsk] = [];
                        const exists = manualEntries[qsk].some(e => e.hcQuestionId === q.id);
                        if (!exists) {
                          manualEntries[qsk].push({
                            id:           "hc_" + q.id + "_agent_" + (entry.createdAt || Date.now()),
                            hcQuestionId: q.id,
                            description:  q.q + ": " + (ans.l || ans.label || ""),
                            notes:        ans.input || "",
                            hcScore:      ans.v !== undefined ? ans.v : (ans.val || 0),
                            hcMax:        q.weight,
                            source:       "health_check",
                            createdAt:    entry.createdAt,
                          });
                        }
                      });
                    } catch(e2) { console.warn("[HC] secondary agent hcAnswers parse:", e2); }
                  }
                }

                if (entry.type === "health_check_entry" && esk &&
                    !manualEntries[esk]?.some(e => e.hcQuestionId === entry.hcQuestionId)) {
                  if (!manualEntries[esk]) manualEntries[esk] = [];
                  manualEntries[esk].push({
                    id: entry.id, hcQuestionId: entry.hcQuestionId,
                    description: entry.description, notes: entry.notes || "",
                    hcScore: entry.hcScore, hcMax: entry.hcMax,
                    source: "health_check", createdAt: entry.createdAt,
                  });
                }
              });
              console.log("[HomeBuddha] secondary agent HC query → HC entries found:", Object.keys(manualEntries).filter(k => manualEntries[k].length > 0));
            }
          } catch(e2) { console.warn("[HomeBuddha] secondary HC query failed:", e2.message); }
        }
      }

      if (!aiReport && aiReports.length > 0) aiReport = aiReports.sort((a,b) => (b.updatedAt||b.createdAt||0)-(a.updatedAt||a.createdAt||0))[0];

      // ── FIX: filteredHomes defined AFTER forEach so portalHomes is fully populated ──
      const filteredHomes = portalHomes.filter(h => !h.deleted);
      const allHomes = filteredHomes.length > 0 ? filteredHomes : aiReports.slice(0,1);
      const activeHomeId = this.state.activeHomeId || (allHomes.length > 0 ? allHomes[0].id : null);

      let referringAgent = null;
      const ud2 = JSON.parse(localStorage.getItem("userDetails") || "{}");
      // ── FIX: activeHome2 uses filteredHomes, not raw portalHomes ──
      const activeHome2 = filteredHomes.length > 0 ? filteredHomes.find(h => h.id === activeHomeId) || filteredHomes[0] : null;
      if (activeHome2 && activeHome2.coUsers) {
        const agentUser = activeHome2.coUsers.find(u => u.role === "agent" || u.role === "lender");
        if (agentUser && agentUser.agentId) {
          try {
            let agentResp = await noAuthgetDetails("getUsers", agentUser.agentId).catch(() => null);
            if (agentResp?.success && agentResp?.data) {
              referringAgent = agentResp.data;
            } else {
              const ar2 = await list("getAgents", { id: agentUser.agentId }).catch(() => null);
              if (ar2?.success && ar2?.data?.length > 0) referringAgent = ar2.data[0];
            }
          } catch(e) { console.warn("coUsers agent load failed:", e.message); }
        }
        if (!referringAgent && agentUser?.email) {
          referringAgent = { email: agentUser.email, firstName: agentUser.name || "Your Agent", lastName: "", phone: agentUser.phone || "" };
        }
      }

      if (!referringAgent) {
        const userId2 = ud2.id || ud2.userId || ud2.userid;
        let freshRefId = ud2.referedById || ud2.invitedBy || ud2.referredByAgent || ud2.referredBy;
        if (!freshRefId && userId2) {
          try {
            const freshUser = await noAuthgetDetails("getUsers", userId2).catch(() => null);
            if (freshUser?.success && freshUser?.data) {
              const u = freshUser.data;
              freshRefId = u.referedById || u.invitedBy || u.referredByAgent || u.referredBy;
            }
            if (!freshRefId) {
              const freshAgent = await noAuthgetDetails("getAgents", userId2).catch(() => null);
              if (freshAgent?.success && freshAgent?.data) {
                const u = freshAgent.data;
                freshRefId = u.referedById || u.invitedBy || u.referredByAgent || u.referredBy;
              }
            }
            if (freshRefId) console.log("[HomeBuddha] fresh user refedById:", freshRefId);
          } catch(e) { console.warn("fresh user fetch failed:", e.message); }
        }

        if (freshRefId && String(freshRefId).length > 10) {
          try {
            let agentResp2 = await noAuthgetDetails("getUsers", freshRefId).catch(() => null);
            if (agentResp2?.success && agentResp2?.data) {
              referringAgent = agentResp2.data;
              console.log("[HomeBuddha] agent found via getUsers/referedById:", referringAgent.firstName);
            } else {
              const ar3 = await list("getAgents", { id: freshRefId }).catch(() => null);
              if (ar3?.success && ar3?.data?.length > 0) {
                referringAgent = ar3.data[0];
                console.log("[HomeBuddha] agent found via getAgents/referedById:", referringAgent.firstName);
              }
            }
          } catch(e) { console.warn("referedById agent load failed:", e.message); }
        }
      }

      this.setState({ loading: false, aiReport, manualEntries, homes: filteredHomes, aiReports, activeHomeId, referringAgent, hcScore: hcScoreFromDB || this.state.hcScore });
    } catch (err) {
      console.error("HomeBuddha loadAll:", err);
      this.setState({ loading: false });
    }
  };


  handleSectionSubmit = async (values, recordId) => {
    try {
      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
      const userId = ud.id || ud.userId;
      if (!userId) { notification.error({ message: "Please log in to save" }); return; }
      let uploadedPhotos = [], photoUrls = [];
      if (values.photos?.length) {
        const uploads = await Promise.all(values.photos.map(async file => {
          if (file.originFileObj) {
            const key = `${userId}_${Date.now()}_${file.name}`;
            const result = await Storage.put(key, file.originFileObj);
            const k = result.key || result;
            return { key: k, url: `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/public/${k}` };
          }
          const match = file.url?.match(/public\/(.*)$/);
          return { key: match ? match[1] : file.name, url: file.url };
        }));
        uploadedPhotos = uploads.map(r => r.key);
        photoUrls = uploads.map(r => r.url);
      }
      const payload = { entityData: { userId, sectionKey: values.sectionKey || this.state.activeKey, subsectionId: values.subsectionId || null, description: values.description, notes: values.notes, brand: values.brand, model: values.model, year: values.year, rating: values.rating, analysisResult: values.analysisResult || "", photos: uploadedPhotos, photoUrls } };
      if (recordId) { await update("mybuddha", recordId, payload); notification.success({ message: "Updated" }); }
      else { await add("mybuddha", payload); notification.success({ message: "Saved" }); }
      await this.loadAll();
    } catch (err) {
      console.error(err);
      notification.error({ message: "Error saving" });
    }
  };

  handleSectionDelete = async (recordId) => {
    try {
      await deleteRecord("mybuddha", recordId);
      notification.success({ message: "Deleted" });
      await this.loadAll();
    } catch (err) { notification.error({ message: "Error deleting" }); }
  };

  // ════════════════════════════════════════════════════════════════════
  //  HOME HEALTH CHECK QUIZ
  // ════════════════════════════════════════════════════════════════════

  hcCaptureEmail = async (email) => {
    if (!email || email.indexOf("@") < 1) return;
    const { hcScore, hcAnswers } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const address = this.state.homes?.find(h => h.id === this.state.activeHomeId)?.address || "";
    try {
      await add("mybuddha", {
        entityData: {
          userId:    ud.id || ud.userId || "",
          type:      "health_check_email",
          email,
          address,
          hcScore:   hcScore?.pct || 0,
          hcAnswers: JSON.stringify(hcAnswers),
          source:    "health_check_portal",
          createdAt: new Date().toISOString(),
        },
      });
      notification.success({ message: "Results sent!", description: "Check your inbox for your home health report." });
    } catch(e) { console.error("hcCaptureEmail:", e); }
  };

  requireLogin = () => {
    const userLogin = localStorage.getItem("userLogin");
    if (userLogin) return true;
    this.props.history.push({ pathname: "/login", state: { from: this.props.location.pathname } });
    return false;
  };
  // Keeps the address bar in sync with whatever section/subsection is
  // currently open, so a specific subsection (e.g. water quality) has a
  // real, copyable URL to put in an ad or post. Only active when mounted
  // via the standalone /homePortal route (HomePortalPage passes
  // enableUrlSync) — the ViewProfile "Home Buddha" tab doesn't set this,
  // so its own URL is left alone.
  syncUrl = (sectionKey, subId) => {
    if (!this.props.enableUrlSync || !this.props.history) return;
    const path = subId ? `/homePortal/${sectionKey}/${subId}` : `/homePortal/${sectionKey}`;
    if (!this.props.location || this.props.location.pathname !== path) {
      this.props.history.replace(path);
    }
  };
  // Pulls the active home's city/state/zip so Home Buddha product/service ads
  // can be geo-targeted like regular site ads. Advertisement.js only reads
  // cityName/stateName/postalCode from props when detailsPageAds is passed —
  // otherwise it ignores them and pulls location from general search filters,
  // which has nothing to do with a specific home's address.
  getActiveHomeGeo = () => {
    const { adGeo } = this.props;
    if (adGeo && (adGeo.cityName || adGeo.stateName || adGeo.postalCode)) {
      return adGeo;
    }
    const { homes, activeHomeId, aiReport } = this.state;
    const home = (homes || []).find(h => h.id === activeHomeId) || aiReport || {};
    const hd = home.homeDetails || {};
    const addressParts = (home.address || "").split(",").map(s => s.trim());
    const cityGuess = addressParts.length >= 2 ? addressParts[1] : "";
    return {
      cityName: hd.city || cityGuess || "",
      stateName: hd.state || "",
      postalCode: hd.zipCode || "",
    };
  };
  loadStepOverrides = () => {
    const { activeHomeId } = this.state;
    const key = "mhb_claim_manual_steps_" + (activeHomeId || "default");
    try {
      const saved = JSON.parse(localStorage.getItem(key) || "{}");
      this.setState({ manualStepOverrides: saved });
    } catch(e) {}
  };

  toggleStepManual = (stepN) => {
    const { activeHomeId, manualStepOverrides } = this.state;
    const key = "mhb_claim_manual_steps_" + (activeHomeId || "default");
    const updated = { ...manualStepOverrides, [stepN]: !manualStepOverrides[stepN] };
    this.setState({ manualStepOverrides: updated });
    try { localStorage.setItem(key, JSON.stringify(updated)); } catch(e) {}
  };

  goToStep = (stepN) => {
    switch(stepN) {
      case 1:
        if (this.props.onShowSetupModal) this.props.onShowSetupModal();
        break;
      case 2:
        if (!localStorage.getItem("userLogin")) {
          this.props.history.push({ pathname: "/login", state: { from: this.props.location.pathname } });
        } else {
          notification.info({ message: "You're already logged in! ✓" });
        }
        break;
      case 3:
        this.hcOpen();
        break;
      case 4:
        this.setState({ activeKey: "maintenance_longevity" });
        this.syncUrl("maintenance_longevity", null);
        break;
      case 5:
        this.props.history.push("/ValueTracker");
        break;
      case 6:
        this.setState({ activeKey: "notes_collaboration" });
        this.syncUrl("notes_collaboration", null);
        break;
      case 7:
        this.setState({ activeKey: "overview" });
        this.syncUrl("overview", null);
        notification.info({ message: "Look for 🛒 Products & Services inside any section below" });
        break;
      case 8:
        this.setState({ activeKey: "home_health", chat_open_home_health: true });
        this.syncUrl("home_health", null);
        break;
      default:
        break;
    }
  };
  getClaimStepStatus = () => {
    const { homes, hcScore, manualEntries, activeHomeId, manualStepOverrides } = this.state;
    const home = (homes || []).find(h => h.id === activeHomeId) || (homes && homes[0]) || null;
    const hasHome = !!home;
    const hasHC = !!hcScore || Object.values(manualEntries || {}).some(arr => Array.isArray(arr) && arr.some(e => e.source === "health_check"));
    const hasValueData = !!(home && home.valueTrackerPrefill && (home.valueTrackerPrefill.purchasePrice || home.valueTrackerPrefill.mortgageBalance));
    const hasGoals = Object.values(manualEntries || {}).some(arr => Array.isArray(arr) && arr.some(e => e.hcQuestionId === "goals"));
    const auto = [ hasHome, hasHome, hasHC, hasHC, hasValueData, hasGoals, true, true ];
    return auto.map((v, i) => v || !!(manualStepOverrides && manualStepOverrides[i+1]));
  };

  renderClaimYourHomeProgress = () => {
    const { homes } = this.state;
    const hasHome = (homes || []).length > 0;
    const status = this.getClaimStepStatus();
    const completedCount = status.filter(Boolean).length;
    const pct = Math.round((completedCount / CLAIM_STEPS.length) * 100);
    return (
      <div style={{ background: WHITE, border: `2px solid ${GOLD}30`, borderRadius: "14px", padding: "18px 20px", marginBottom: "16px" }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"4px", flexWrap:"wrap", gap:"8px" }}>
          <div>
            <div style={{ fontSize:"11px", fontWeight:700, color: GOLD, textTransform:"uppercase", letterSpacing:".06em" }}>Claim Your Home™</div>
            <div style={{ fontSize:"16px", fontWeight:800, color: NAVY }}>Your Home Operating System</div>
          </div>
          <div style={{ fontSize:"13px", fontWeight:800, color: NAVY }}>{completedCount} / {CLAIM_STEPS.length} steps</div>
        </div>
        <div style={{ background:"#EDF2F7", borderRadius:"50px", height:"8px", overflow:"hidden", marginBottom:"14px" }}>
          <div style={{ background:`linear-gradient(90deg, ${GOLD}, ${NAVY})`, height:"100%", width: pct + "%", borderRadius:"50px", transition:"width .4s" }} />
        </div>
        <Row gutter={[8,8]}>
          {CLAIM_STEPS.map((s, i) => {
            const done = status[i];
            return (
              <Col key={s.n} xs={12} sm={6}>
                <div onClick={() => this.goToStep(s.n)}
                  style={{ background: done ? "#F0FFF4" : LIGHT, border: `1px solid ${done ? "#9AE6B4" : BORDER}`, borderRadius:"10px", padding:"10px 10px", height:"100%", cursor:"pointer", position:"relative", transition:"transform .12s" }}
                  onMouseEnter={e => e.currentTarget.style.transform = "scale(1.02)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
                  <div onClick={e => { e.stopPropagation(); this.toggleStepManual(s.n); }}
                    title={done ? "Mark as not done" : "Mark as done"}
                    style={{ position:"absolute", top:"8px", right:"8px", width:"18px", height:"18px", borderRadius:"5px", border:`2px solid ${done ? "#38A169" : "#CBD5E0"}`, background: done ? "#38A169" : "white", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" }}>
                    {done && <span style={{ color:"white", fontSize:"11px", fontWeight:900, lineHeight:1 }}>✓</span>}
                  </div>
                  <div style={{ fontSize:"18px" }}>{done ? "✅" : s.icon}</div>
                  <div style={{ fontSize:"11px", fontWeight:700, color: done ? "#276749" : NAVY, marginTop:"4px", paddingRight:"18px" }}>{s.n}. {s.label}</div>
                  <div style={{ fontSize:"10px", color: MUTED, marginTop:"2px", lineHeight:1.4 }}>{s.desc}</div>
                  <div style={{ fontSize:"9px", color: NAVY, marginTop:"6px", fontWeight:700 }}>{done ? "Revisit →" : "Start →"}</div>
                </div>
              </Col>
            );
          })}
        </Row>
        {!hasHome && (
          <div style={{ marginTop:"14px", textAlign:"center" }}>
            <div style={{ fontSize:"12px", color: MUTED, marginBottom:"8px" }}>You haven't claimed a home yet — start with Step 1.</div>
            <Button type="primary" onClick={() => { if (this.props.onShowSetupModal) this.props.onShowSetupModal(); }} style={{ borderRadius:"50px", background: NAVY, border:"none", fontWeight:700 }}>📍 Claim Your Home Now</Button>
          </div>
        )}
        <div style={{ marginTop:"16px", background:"linear-gradient(135deg,#744210,#9A5A00)", borderRadius:"12px", padding:"16px 18px", display:"flex", alignItems:"center", gap:"14px", flexWrap:"wrap" }}>
          <div style={{ fontSize:"30px", flexShrink:0 }}>🏅</div>
          <div style={{ flex:1, minWidth:"220px" }}>
            <div style={{ color:"#FFD98E", fontWeight:800, fontSize:"13px", marginBottom:"3px" }}>Want the pros to handle it?</div>
            <div style={{ color:"rgba(255,255,255,0.8)", fontSize:"12px", lineHeight:1.6 }}>A Certified In-Home Health Evaluation is normally <strong>$299</strong> — <strong style={{color:"#FFD98E"}}>FREE for Pioneer Homeowners</strong> right now. Or keep going below and build it yourself, step by step, for free.</div>
          </div>
          <a href="https://go.myhomebuddha.com/homeowner" target="_blank" rel="noopener noreferrer"
            style={{ flexShrink:0, padding:"9px 18px", background:"#D4921A", color:"#2A1600", borderRadius:"50px", fontWeight:800, fontSize:"12px", textDecoration:"none", whiteSpace:"nowrap" }}>
            📞 Book My FREE Evaluation
          </a>
        </div>
      </div>
    );
  };
  hcOpen = () => {
    if (!this.requireLogin()) return;
    this.setState({ hcOpen: true, hcStep: -1, hcAnswers: {}, hcScore: null });
  };
  hcClose = () => this.setState({ hcOpen: false });
  hcRestart = () => this.setState({ hcStep: -1, hcAnswers: {}, hcScore: null });

  hcSelectOpt = (qId, opt) => {
    this.setState(prev => ({ hcAnswers: { ...prev.hcAnswers, [qId]: opt } }));
  };

  hcSetInput = (qId, val) => {
    this.setState(prev => {
      const ans = prev.hcAnswers[qId] || {};
      return { hcAnswers: { ...prev.hcAnswers, [qId]: { ...ans, input: val } } };
    });
  };

  hcNext = () => {
    const { hcStep, hcAnswers } = this.state;
    if (hcStep === -1) { this.setState({ hcStep: 0 }); return; }
    const q = HC_QUESTIONS[hcStep];
    if (!hcAnswers[q.id]) {
      this.setState(prev => ({ hcAnswers: { ...prev.hcAnswers, [q.id]: { v: 0, l: 'Skipped', skipped: true } } }));
    }
    if (hcStep < HC_QUESTIONS.length - 1) { this.setState({ hcStep: hcStep + 1 }); }
    else { this.hcFinish(); }
  };

  hcBack = () => {
    const { hcStep } = this.state;
    if (hcStep <= 0) { this.setState({ hcStep: -1 }); return; }
    this.setState({ hcStep: hcStep - 1 });
  };

  hcFinish = async () => {
    const { hcAnswers } = this.state;
    const score = hcCalcPortalScores(hcAnswers);
    const flagged = this.hcComputeFlagged(hcAnswers);
    const priorityChoices = {};
    flagged.forEach(f => { priorityChoices[f.id] = { remind: true, help: false }; });
    this.setState({ hcStep: 99, hcScore: score, hcFlagged: flagged, hcPriorityChoices: priorityChoices });
    await this.hcSaveToPortal(hcAnswers, score);
    this.hcGenerateSummary(score, flagged);
  };
hcComputeFlagged = (answers) => {
    const flagged = [];
    HC_QUESTIONS.forEach(q => {
      const ans = answers[q.id];
      if (!ans || ans.skipped) return;
      const ratio = (ans.v || 0) / (q.weight || 4);
      if (ratio <= 0.5) {
        flagged.push({
          id: q.id, section: q.section, sLabel: q.sLabel, sColor: q.sColor,
          question: q.q, answerLabel: ans.l, ratio,
          severity: ratio === 0 ? "critical" : ratio <= 0.25 ? "high" : "moderate",
        });
      }
    });
    return flagged.sort((a, b) => a.ratio - b.ratio);
  };
  hcGenerateSummary = async (score, flagged) => {
    this.setState({ hcSummaryLoading: true, hcSummaryText: "" });
    const address = this.state.homes?.find(h => h.id === this.state.activeHomeId)?.address || "your home";
    const flaggedText = flagged.slice(0, 8).map(f => `${f.sLabel} — ${f.question}: ${f.answerLabel}`).join("\n");
    try {
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: AI_MODEL, max_tokens: 500,
          messages: [{ role: "user", content:
            "You are My Home Buddha AI. A homeowner at " + address + " just completed a 29-question home health check-up and scored " + score.pct + "%.\n\n" +
            "Areas they scored lowest on:\n" + (flaggedText || "None — great job across the board!") + "\n\n" +
            "Write a warm, encouraging response in exactly 2 short paragraphs, plain text, no headers or bullets: paragraph 1 acknowledges their overall status in plain language; paragraph 2 names their top 2-3 priorities from the list above and briefly explains why each matters." }],
        }),
      });
      const data = await response.json();
      this.setState({ hcSummaryLoading: false, hcSummaryText: data.content?.[0]?.text || "" });
    } catch(e) {
      console.error("hcGenerateSummary:", e);
      this.setState({ hcSummaryLoading: false });
    }
  };
  hcTogglePriorityAction = (qId, key) => {
    this.setState(prev => ({
      hcPriorityChoices: { ...prev.hcPriorityChoices, [qId]: { ...prev.hcPriorityChoices[qId], [key]: !prev.hcPriorityChoices[qId]?.[key] } },
    }));
  };
  hcSubmitPriorityActions = async () => {
    const { hcFlagged, hcPriorityChoices, activeHomeId, homes } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId || ud.userid;
    const home = homes.find(h => h.id === activeHomeId) || homes[0];
    const address = home?.address || home?.nickname || "";
    this.setState({ hcActionsSaving: true });
    let reminderCount = 0, helpCount = 0;
    try {
      const jobs = [];
      hcFlagged.forEach(f => {
        const choice = hcPriorityChoices[f.id] || {};
        if (choice.remind) {
          reminderCount++;
          const daysOut = f.severity === "critical" ? 7 : f.severity === "high" ? 14 : 30;
          const remindDate = new Date(Date.now() + daysOut * 86400000).toISOString().split("T")[0];
          jobs.push(add("mybuddha", { entityData: {
            userId, type: "home_reminder", action: "home_reminder",
            sectionKey: f.section, sectionLabel: f.sLabel, subLabel: f.question,
            reminderDate: remindDate, reminderTime: "09:00", repeatFrequency: "none",
            note: "Flagged from Home Health Check: " + f.answerLabel,
            address, homeId: activeHomeId || "", status: "pending", createdAt: Date.now(),
            contactEmail: ud.email || "", contactName: `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
            contactPhone: ud.phone || ud.phno || "",
          }}));
        }
        if (choice.help) {
          helpCount++;
          jobs.push(add("notifications", { entityData: {
            notificationType: "vendor_request", leadType: "vendor", source: "health_check_priorities",
            serviceCategory: f.sLabel, sectionKey: f.section, subsectionLabel: f.question,
            helpType: "quote", message: "Flagged from Home Health Check: " + f.question + " — " + f.answerLabel,
            address, yearBuilt: home?.homeDetails?.yearBuilt || "", sqFootage: home?.homeDetails?.squareFeet || "",
            homeType: home?.homeDetails?.propertyType || "",
            requestorDetails: { name: `${ud.firstName || ""} ${ud.lastName || ""}`.trim(), email: ud.email || "", phone: ud.phone || ud.phno || "" },
            isAssigned: false, assignedStatus: "pending", createdAt: Date.now(), userId,
          }}));
        }
      });
      await Promise.all(jobs);
      notification.success({ message: "You're all set!", description: reminderCount + " reminder(s) scheduled" + (helpCount ? " and " + helpCount + " help request(s) submitted." : ".") });
      await this.loadAll();
      this.setState({ hcActionsSaving: false, hcOpen: false });
    } catch(e) {
      console.error("hcSubmitPriorityActions:", e);
      notification.error({ message: "Something went wrong — please try again" });
      this.setState({ hcActionsSaving: false });
    }
  };
  // Lets a returning user (who already has HC data saved) revisit priorities
  // without retaking the quiz — called from the score banner.
  hcOpenPriorities = () => {
    if (!this.requireLogin()) return;
    const { manualEntries, hcScore } = this.state;
    const flagged = [];
    Object.keys(manualEntries).forEach(sk => {
      (manualEntries[sk] || []).forEach(e => {
        if (e.source !== "health_check" || !e.hcQuestionId) return;
        const ratio = (e.hcScore || 0) / (e.hcMax || 4);
        if (ratio <= 0.5) {
          const q = HC_QUESTIONS.find(x => x.id === e.hcQuestionId);
          flagged.push({
            id: e.hcQuestionId, section: sk, sLabel: q ? q.sLabel : sk, sColor: q ? q.sColor : NAVY,
            question: q ? q.q : e.description, answerLabel: (e.description || "").split(": ").slice(1).join(": ") || e.description,
            ratio, severity: ratio === 0 ? "critical" : ratio <= 0.25 ? "high" : "moderate",
          });
        }
      });
    });
    flagged.sort((a, b) => a.ratio - b.ratio);
    const priorityChoices = {};
    flagged.forEach(f => { priorityChoices[f.id] = { remind: true, help: false }; });
    this.setState({ hcOpen: true, hcStep: 100, hcFlagged: flagged, hcPriorityChoices: priorityChoices });
    this.hcGenerateSummary(hcScore || { pct: 0 }, flagged);
  };
  hcSaveToPortal = async (answers, score) => {
    const { activeHomeId, homes } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId || ud.userid;
    if (!userId) return;
    this.setState({ hcSaving: true });
    try {
      const newEntries = { ...this.state.manualEntries };
      HC_QUESTIONS.forEach(q => {
        const ans = answers[q.id];
        if (!ans || ans.skipped) return;
        const key = q.section;
        if (!newEntries[key]) newEntries[key] = [];
        const existing = newEntries[key].findIndex(e => e.hcQuestionId === q.id);
        const entry = {
          id:           "hc_" + q.id + "_" + Date.now(),
          hcQuestionId: q.id,
          description:  q.q + ": " + ans.l,
          notes:        ans.input || "",
          hcScore:      ans.v,
          hcMax:        q.weight,
          createdAt:    new Date().toISOString(),
          source:       "health_check",
        };
        if (existing >= 0) { newEntries[key][existing] = entry; }
        else { newEntries[key] = [...(newEntries[key] || []), entry]; }
      });
      const home = homes.find(h => h.id === activeHomeId);
      const address = home?.address || home?.nickname || "";
      await add("mybuddha", {
        entityData: {
          userId, type: "health_check", action: "health_check", address, homeId: activeHomeId,
          hcScore: score.pct, hcRaw: score.total, hcMaxRaw: score.maxRaw,
          hcAnswers: JSON.stringify(answers), hcSectionScores: JSON.stringify(score.bySec),
          hcTakenAt: new Date().toISOString(),
        },
      });
      const savePromises = HC_QUESTIONS.map(async (q) => {
        const ans = answers[q.id];
        if (!ans || ans.skipped) return;
        return add("mybuddha", {
          entityData: {
            userId, sectionKey: q.section, type: "health_check_entry", action: "health_check_entry",
            hcQuestionId: q.id, description: q.q + ": " + ans.l, notes: ans.input || "",
            hcScore: ans.v, hcMax: q.weight, source: "health_check",
            homeId: activeHomeId, address,
          },
        });
      });
      await Promise.all(savePromises);
      this.setState({ manualEntries: newEntries, hcSaving: false });
      notification.success({ message: "Health check saved!", description: "Your answers have been added to each section of your home portal." });
    } catch(e) {
      console.error("hcSaveToPortal:", e);
      this.setState({ hcSaving: false });
    }
  };


  renderHCBanner = () => {
    const { manualEntries } = this.state;
    const hasTaken = Object.values(manualEntries).some(arr => arr.some && arr.some(e => e.source === "health_check"));
    if (hasTaken) return null;
    return (
      <div onClick={this.hcOpen} style={{ background: "linear-gradient(135deg,#C53030,#9B2C2C)", borderRadius: "12px", padding: "16px 20px", marginBottom: "14px", cursor: "pointer", display: "flex", alignItems: "center", gap: "14px", boxShadow: "0 2px 12px rgba(197,48,48,0.3)", transition: "transform 0.15s" }}
        onMouseEnter={e => e.currentTarget.style.transform = "scale(1.01)"}
        onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}>
        <div style={{ fontSize: "36px", flexShrink: 0 }}>😟</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "14px", marginBottom: "3px" }}>🏠 How safe and healthy is your home?</div>
          <div style={{ color: "rgba(255,255,255,0.85)", fontSize: "12px", lineHeight: 1.6 }}>Take the <strong>Happy Home Buddha Health Check-Up</strong> — 29 questions, 5 minutes, and you'll know exactly where your home stands. Most homeowners are surprised by what they find.</div>
        </div>
        <div style={{ flexShrink: 0, background: "#D4921A", color: "#2A1600", padding: "10px 18px", borderRadius: "50px", fontWeight: 800, fontSize: "13px", whiteSpace: "nowrap" }}>Start Free →</div>
      </div>
    );
  };

  renderHCScoreBanner = () => {
    const { manualEntries, hcScore } = this.state;
    let savedScore = hcScore;
    if (!savedScore) {
      let totalEarned = 0;
      HC_QUESTIONS.forEach(q => {
        const entries = manualEntries[q.section] || [];
        const entry = entries.find(e => e.hcQuestionId === q.id);
        if (entry) { totalEarned += (entry.hcScore || 0); }
      });
      if (totalEarned > 0) {
        const pct = Math.min(90, Math.round((totalEarned / HC_MAX_RAW) * 90));
        savedScore = { pct, total: totalEarned, maxRaw: HC_MAX_RAW };
      }
    }
    if (!savedScore) return null;
    const mood = hcBuddhaLevel(savedScore.pct);
    return (
      <div style={{ background: "linear-gradient(135deg,#0F1560,#1A2280)", borderRadius: "12px", padding: "14px 18px", marginBottom: "14px", borderLeft: `4px solid ${mood.color}`, display: "flex", alignItems: "center", gap: "14px" }}>
        <div style={{ fontSize: "32px" }}>{mood.emoji}</div>
        <div style={{ flex: 1 }}>
          <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "13px", marginBottom: "2px" }}>
            Your Home Health Score: <span style={{ fontSize: "22px", color: mood.color }}>{savedScore.pct}%</span>
            <span style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px", fontWeight: 400, marginLeft: "8px" }}>out of 90%</span>
          </div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px" }}>{mood.label} · Tap each section tab to see your answers and add photos to improve your score</div>
        </div>
        <Button size="small" onClick={this.hcOpenPriorities} style={{ borderRadius: "50px", borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.8)", background: "transparent", flexShrink: 0, fontSize: "11px", marginRight: "6px" }}>🎯 Priorities</Button>
        <Button size="small" onClick={this.hcOpen} style={{ borderRadius: "50px", borderColor: "rgba(255,255,255,0.3)", color: "rgba(255,255,255,0.8)", background: "transparent", flexShrink: 0, fontSize: "11px" }}>Retake</Button>
      </div>
    );
  };

  renderHCModal = () => {
    const { hcOpen, hcStep } = this.state;
    if (!hcOpen) return null;
    return (
      <Modal visible={hcOpen} onCancel={this.hcClose} footer={null} width={620} style={{ top: 20 }} bodyStyle={{ padding: 0 }} className="modal-black-close" title={null} closable={true}>
        {hcStep === -1  && this.renderHCIntro()}
        {hcStep >= 0 && hcStep < HC_QUESTIONS.length && this.renderHCQuestion(hcStep)}
        {hcStep === 99  && this.renderHCResults()}
        {hcStep === 100 && this.renderHCPriorities()}

      </Modal>
    );
  };

  renderHCIntro = () => (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      <div style={{ background: "linear-gradient(135deg,#C53030,#9B2C2C)", padding: "28px", textAlign: "center", borderRadius: "8px 8px 0 0" }}>
        <div style={{ fontSize: "52px", marginBottom: "8px" }}>🏠</div>
        <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "20px", marginBottom: "6px" }}>Happy Home Buddha</div>
        <div style={{ color: "white", fontWeight: 700, fontSize: "15px", marginBottom: "10px" }}>Home Health Check-Up</div>
        <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "13px", lineHeight: 1.7, maxWidth: "400px", margin: "0 auto" }}>
          29 questions · 5–7 minutes · personalized score out of 90%<br/>
          <strong style={{ color: "#FFD98E" }}>Your answers will be saved directly into your home portal.</strong>
        </div>
      </div>
      <div style={{ padding: "24px" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "20px" }}>
          {["🏅 Home health score out of 80%", "📊 Section-by-section breakdown", "📋 Prioritized action plan", "🔔 Answers saved to your portal", "💰 Estimated repair costs", "🤝 Connect with professionals"].map((b, i) => (
            <div key={i} style={{ background: "#F7F9FC", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: "#2D3748", display: "flex", alignItems: "center", gap: "6px" }}>{b}</div>
          ))}
        </div>
        <div style={{ background: "linear-gradient(135deg,#744210,#9A5A00)", borderRadius: "10px", padding: "12px 16px", marginBottom: "20px", textAlign: "center" }}>
          <span style={{ color: "#FFD98E", fontSize: "12px", fontWeight: 700 }}>⭐ Maximum self-reported score is <strong>90%</strong> — the final 10% requires a Certified Home Health Inspection</span>
        </div>
        <Button type="primary" block onClick={this.hcNext} style={{ height: "52px", borderRadius: "50px", background: "#C53030", borderColor: "#C53030", fontSize: "16px", fontWeight: 800 }}>
          🚀 Start My Health Check-Up
        </Button>
      </div>
    </div>
  );

  renderHCQuestion = (stepIdx) => {
    const q = HC_QUESTIONS[stepIdx];
    const ans = this.state.hcAnswers[q.id];
    const pct = Math.round(((stepIdx + 1) / HC_QUESTIONS.length) * 100);
    const isLast = stepIdx === HC_QUESTIONS.length - 1;
    const encouragements = ["Great start! 🏁", "Keep going! 💪", "You're doing great! ⭐", "Halfway there! 🌟", "Almost done! 🎯", "Final stretch! 🚀", "Last question! 🏆"];
    const enc = encouragements[Math.min(Math.floor(pct / 15), encouragements.length - 1)];
    return (
      <div style={{ fontFamily: "DM Sans, sans-serif" }}>
        <div style={{ background: q.sColor, padding: "14px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "16px" }}>{q.sIcon}</span>
              <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em" }}>{q.sLabel}</span>
            </div>
            <span style={{ color: "#FFD98E", fontSize: "11px", fontWeight: 700 }}>{stepIdx + 1} / {HC_QUESTIONS.length}</span>
          </div>
          <div style={{ background: "rgba(255,255,255,0.2)", borderRadius: "50px", height: "5px", overflow: "hidden", marginBottom: "6px" }}>
            <div style={{ background: "#FFD98E", height: "100%", borderRadius: "50px", width: pct + "%", transition: "width 0.4s ease" }} />
          </div>
          <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "10px", textAlign: "center", fontStyle: "italic" }}>{enc}</div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
            <div style={{ fontSize: "17px", fontWeight: 800, color: "#1A2280", lineHeight: 1.3 }}>{q.q}</div>
          </div>
          <div style={{ fontSize: "12px", color: "#718096", lineHeight: 1.6, marginBottom: "14px", padding: "10px 14px", background: "#F7F9FC", borderRadius: "8px", borderLeft: `3px solid ${q.sColor}` }}>{q.hint}</div>
          <div style={{ marginBottom: "14px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#4A5568", marginBottom: "5px" }}>{q.inputLabel} <span style={{ fontWeight: 400, color: "#A0AEC0" }}>(optional)</span></div>
            <Input placeholder="Enter details for a more accurate score..." value={(ans && ans.input) || ""} onChange={e => this.hcSetInput(q.id, e.target.value)} style={{ borderRadius: "8px", fontSize: "13px" }} />
          </div>
          <div style={{ fontSize: "11px", fontWeight: 700, color: "#4A5568", marginBottom: "8px" }}>Select the best description:</div>
          <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginBottom: "20px" }}>
            {q.opts.map((opt, oi) => {
              const selected = ans && ans.l === opt.l;
              return (
                <div key={oi} onClick={() => this.hcSelectOpt(q.id, opt)} style={{ padding: "11px 14px", borderRadius: "10px", border: `2px solid ${selected ? q.sColor : "#E2E8F0"}`, cursor: "pointer", background: selected ? q.sColor + "12" : "white", transition: "all 0.15s" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <div style={{ width: "18px", height: "18px", borderRadius: "50%", border: `2px solid ${selected ? q.sColor : "#CBD5E0"}`, flexShrink: 0, background: selected ? q.sColor : "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {selected && <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: "white" }} />}
                    </div>
                    <span style={{ fontSize: "13px", fontWeight: selected ? 700 : 400, color: selected ? q.sColor : "#2D3748", lineHeight: 1.4 }}>{opt.l}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button onClick={this.hcBack} style={{ borderRadius: "50px", borderColor: "#E2E8F0", color: "#718096" }}>← Back</Button>
            <Button type="primary" onClick={this.hcNext} style={{ borderRadius: "50px", background: ans ? q.sColor : "#CBD5E0", borderColor: ans ? q.sColor : "#CBD5E0", fontWeight: 800, minWidth: "120px" }}>
              {isLast ? "🎯 Get My Score" : "Next →"}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  renderHCResults = () => {
    const { hcScore, hcSaving } = this.state;
    if (!hcScore) return null;
    const mood = hcBuddhaLevel(hcScore.pct);
    const secArr = Object.values(hcScore.bySec).sort((a, b) => a.pct - b.pct);
    return (
      <div style={{ fontFamily: "DM Sans, sans-serif" }}>
        <div style={{ background: "linear-gradient(135deg,#0F1560,#1A2280)", padding: "28px", textAlign: "center", borderRadius: "8px 8px 0 0" }}>
          <div style={{ fontSize: "52px", marginBottom: "8px" }}>{mood.emoji}</div>
          <div style={{ color: "#FFD98E", fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".1em", marginBottom: "4px" }}>Your Home Health Score</div>
          <div style={{ fontSize: "60px", fontWeight: 900, color: "white", lineHeight: 1 }}>{hcScore.pct}<span style={{ fontSize: "24px", color: "#FFD98E" }}>%</span></div>
          <div style={{ fontSize: "15px", fontWeight: 700, color: mood.color, margin: "6px 0 10px" }}>{mood.label}</div>
          <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: "50px", height: "8px", overflow: "hidden", marginBottom: "6px" }}>
            <div style={{ background: `linear-gradient(90deg,${mood.color},#FFD98E)`, height: "100%", borderRadius: "50px", width: hcScore.pct + "%" }} />
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "10px" }}>0% — 90% max self-reported — 100% with certified inspection</div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          <div style={{ fontWeight: 800, color: "#1A2280", fontSize: "14px", marginBottom: "12px" }}>📊 Score by Section</div>
          {secArr.map((sec, i) => {
            const barColor = sec.pct >= 58 ? "#38A169" : sec.pct >= 44 ? "#D97706" : "#C53030";
            return (
              <div key={i} style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#2D3748" }}>{sec.icon} {sec.label}</span>
                  <span style={{ fontSize: "12px", fontWeight: 800, color: barColor }}>{sec.pct}%</span>
                </div>
                <div style={{ background: "#EDF2F7", borderRadius: "50px", height: "6px", overflow: "hidden" }}>
                  <div style={{ background: barColor, height: "100%", borderRadius: "50px", width: sec.pct + "%" }} />
                </div>
              </div>
            );
          })}
          <div style={{ background: "#F0FFF4", border: "1px solid #9AE6B4", borderRadius: "10px", padding: "14px 16px", margin: "16px 0", display: "flex", alignItems: "center", gap: "10px" }}>
            <CheckCircleOutlined style={{ color: "#38A169", fontSize: "18px", flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: "13px", fontWeight: 700, color: "#276749" }}>{hcSaving ? "Saving your answers to your portal..." : "Answers saved to your Home Portal ✓"}</div>
              <div style={{ fontSize: "11px", color: "#4A5568", marginTop: "2px" }}>Your answers are now in each section — click any section tab to see them, add photos, and boost your score</div>
            </div>
          </div>
          <div style={{ background: "white", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "14px 16px", margin: "0 0 14px" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#1A2280", marginBottom: "4px" }}>📧 Email me my full results & action plan</div>
            <div style={{ fontSize: "11px", color: "#718096", marginBottom: "10px" }}>Get a detailed breakdown with repair costs and improvement tips sent to your inbox.</div>
            <div style={{ display: "flex", gap: "8px" }}>
              <Input type="email" placeholder="Your email address" id="hcPortalEmailInput" style={{ borderRadius: "8px", fontSize: "13px", flex: 1 }} onBlur={e => { if (e.target.value) this.hcCaptureEmail(e.target.value); }} />
              <Button type="primary" onClick={() => { const el = document.getElementById("hcPortalEmailInput"); if (el && el.value) this.hcCaptureEmail(el.value); }} style={{ borderRadius: "8px", background: "#1A2280", borderColor: "#1A2280", fontWeight: 700 }}>Send</Button>
            </div>
          </div>
          <div style={{ background: "linear-gradient(135deg,#744210,#9A5A00)", borderRadius: "12px", padding: "16px", marginBottom: "14px", textAlign: "center" }}>
            <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "13px", marginBottom: "6px" }}>🏅 Unlock the Final 10%</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", lineHeight: 1.6, marginBottom: "12px" }}>A Certified Home Health Inspection uncovers what you can't see — hidden moisture, aging wiring, code issues, and more.</div>
            <Button style={{ borderRadius: "50px", background: "#D4921A", borderColor: "#D4921A", color: "#2A1600", fontWeight: 800 }} onClick={() => window.open("https://go.myhomebuddha.com/homeowner", "_blank")}>📞 Book a Certified Inspection</Button>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <Button block onClick={this.hcRestart} style={{ borderRadius: "50px", borderColor: "#E2E8F0", color: "#718096" }}>🔄 Retake</Button>
            <Button block type="primary" onClick={() => this.setState({ hcStep: 100 })} style={{ borderRadius: "50px", background: "#1A2280", borderColor: "#1A2280", fontWeight: 700 }}>See My Priorities →</Button>
          </div>
        </div>
      </div>
    );
  };
renderHCPriorities = () => {
    const { hcFlagged, hcPriorityChoices, hcSummaryText, hcSummaryLoading, hcActionsSaving } = this.state;
    const anySelected = hcFlagged.some(f => hcPriorityChoices[f.id]?.remind || hcPriorityChoices[f.id]?.help);
    return (
      <div style={{ fontFamily: "DM Sans, sans-serif" }}>
        <div style={{ background: "linear-gradient(135deg,#0F1560,#1A2280)", padding: "24px", textAlign: "center", borderRadius: "8px 8px 0 0" }}>
          <div style={{ fontSize: "40px", marginBottom: "6px" }}>🎯</div>
          <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "17px" }}>Your Priorities & Next Steps</div>
        </div>
        <div style={{ padding: "20px 24px" }}>
          <div style={{ background: "#F7F9FC", borderRadius: "10px", padding: "14px 16px", marginBottom: "18px", minHeight: "60px" }}>
            {hcSummaryLoading ? (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", color: MUTED, fontSize: "12px" }}><Spin size="small" /> Your Home Buddha is thinking this through...</div>
            ) : (
              <div style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.7, whiteSpace: "pre-line" }}>{hcSummaryText || "Nice work — nothing urgent stood out in your answers."}</div>
            )}
          </div>
          {hcFlagged.length === 0 ? (
            <div style={{ textAlign: "center", padding: "10px 0 20px" }}>
              <div style={{ fontSize: "13px", color: MUTED }}>Nothing flagged right now. Check back after your next update.</div>
            </div>
          ) : (
            <div style={{ marginBottom: "18px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>Pick what you'd like set up for each item</div>
              {hcFlagged.map(f => {
                const choice = hcPriorityChoices[f.id] || {};
                return (
                  <div key={f.id} style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "12px 14px", marginBottom: "8px" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: f.sColor, textTransform: "uppercase", marginBottom: "2px" }}>{f.sLabel}</div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#1A202C" }}>{f.question}</div>
                    <div style={{ fontSize: "12px", color: MUTED, marginBottom: "8px" }}>{f.answerLabel}</div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button onClick={() => this.hcTogglePriorityAction(f.id, "remind")}
                        style={{ flex: 1, padding: "7px 10px", borderRadius: "50px", border: `2px solid ${choice.remind ? "#276749" : BORDER}`, background: choice.remind ? "#F0FFF4" : WHITE, color: choice.remind ? "#276749" : MUTED, fontSize: "11px", fontWeight: 700, cursor: "pointer" }}>
                        ⏰ Remind Me
                      </button>
                      <button onClick={() => this.hcTogglePriorityAction(f.id, "help")}
                        style={{ flex: 1, padding: "7px 10px", borderRadius: "50px", border: `2px solid ${choice.help ? NAVY : BORDER}`, background: choice.help ? `${NAVY}10` : WHITE, color: choice.help ? NAVY : MUTED, fontSize: "11px", fontWeight: 700, cursor: "pointer" }}>
                        🛠 Get Me Help
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", gap: "8px" }}>
            <Button block onClick={() => this.setState({ hcOpen: false })} style={{ borderRadius: "50px", borderColor: BORDER, color: MUTED }}>Skip for now</Button>
            {hcFlagged.length > 0 && (
              <Button block type="primary" loading={hcActionsSaving} disabled={!anySelected} onClick={this.hcSubmitPriorityActions}
                style={{ borderRadius: "50px", background: NAVY, borderColor: NAVY, fontWeight: 800 }}>
                Set It Up →
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  analyzeSubsection = async (sectionKey, sub) => {
    const subKey = sectionKey + ":" + sub.id;
    const section = SECTIONS.find(s => s.key === sectionKey);
    const { manualEntries, aiReport } = this.state;
    const sectionEntries = manualEntries[sectionKey] || [];
    const aiData = this.getAIDataForSection(sectionKey);
    const address = aiReport?.address || "this home";
    this.setState(s => ({ subAnalyzing: { ...s.subAnalyzing, [subKey]: true }, subAnalysis: { ...s.subAnalysis, [subKey]: "" } }));
    try {
      const context = [
        "Property: " + address,
        "Section: " + (section ? section.label : ""),
        "Subsection: " + sub.label,
        aiData ? "AI Report data: " + JSON.stringify(aiData).slice(0, 600) : "",
        sectionEntries.length > 0 ? "User notes: " + sectionEntries.map(e => e.description || e.notes || "").filter(Boolean).join("; ") : "",
      ].filter(Boolean).join("\n");
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: AI_MODEL, max_tokens: 800, messages: [{ role: "user", content: "You are My Home Buddha AI — a home health expert. Analyze the \"" + sub.label + "\" subsection for " + address + ".\n\n" + context + "\n\nProvide:\n1. Current status assessment\n2. Key risks or concerns\n3. Recommended actions (prioritized)\n4. Estimated cost range if applicable\n\nBe specific and practical. 3-4 paragraphs max." }] }),
      });
      const data = await response.json();
      const result = data.content?.[0]?.text || "Analysis unavailable — please try again.";
      this.setState(s => ({ subAnalyzing: { ...s.subAnalyzing, [subKey]: false }, subAnalysis: { ...s.subAnalysis, [subKey]: result } }));
    } catch(err) {
      this.setState(s => ({ subAnalyzing: { ...s.subAnalyzing, [subKey]: false } }));
      notification.error({ message: "Analysis failed — please try again" });
    }
  };

  sendSubChat = async (sectionKey, subId) => {
    const subKey = sectionKey + ":" + subId;
    const msg = (this.state.subChatInput[subKey] || "").trim();
    if (!msg) return;
    const section = SECTIONS.find(s => s.key === sectionKey);
    const sub = section?.subsections?.find(s => s.id === subId);
    const aiData = this.getAIDataForSection(sectionKey);
    const address = this.state.aiReport?.address || "this home";
    const systemPrompt = "You are My Home Buddha AI — a home health expert advising on the \"" + (sub ? sub.label : subId) + "\" component of " + address + ". " + (aiData ? "AI Report context: " + JSON.stringify(aiData).slice(0,500) : "") + " Be specific, practical, and concise.";
    const prev = this.state.subChatMessages[subKey] || [];
    const messages = [...prev, { role: "user", content: msg }];
    this.setState(s => ({ subChatMessages: { ...s.subChatMessages, [subKey]: messages }, subChatInput: { ...s.subChatInput, [subKey]: "" }, subChatLoading: { ...s.subChatLoading, [subKey]: true } }));
    try {
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: AI_MODEL, max_tokens: 600, system: systemPrompt, messages }),
      });
      const data = await response.json();
      const reply = data.content?.[0]?.text || "I couldn't respond — please try again.";
      this.setState(s => ({ subChatMessages: { ...s.subChatMessages, [subKey]: [...messages, { role: "assistant", content: reply }] }, subChatLoading: { ...s.subChatLoading, [subKey]: false } }));
    } catch(err) {
      this.setState(s => ({ subChatLoading: { ...s.subChatLoading, [subKey]: false } }));
    }
  };

  sendChat = async (sectionKey) => {
    const userMsg = (this.state.chatInput[sectionKey] || "").trim();
    if (!userMsg) return;
    const section = SECTIONS.find(s => s.key === sectionKey);
    const aiReport = this.state.aiReport;
    const aiData   = this.getAIDataForSection(sectionKey);
    const context  = aiData ? `AI Analysis for ${section.label}: ${JSON.stringify(aiData).slice(0, 1000)}` : `Section: ${section.label}. No AI report available yet.`;
    const homeAddr = aiReport?.address || "this home";
    const systemPrompt = `You are My Home Buddha AI — a warm, knowledgeable home health advisor. \nYou're helping a homeowner with the "${section.label}" section of their home portal for ${homeAddr}.\n${context}\nGive practical, specific advice. Be encouraging but honest. Keep responses concise (2-4 paragraphs max).`;
    const prev = this.state.chatMessages[sectionKey] || [];
    const newMessages = [...prev, { role: "user", content: userMsg }];
    this.setState(s => ({ chatMessages: { ...s.chatMessages, [sectionKey]: newMessages }, chatInput: { ...s.chatInput, [sectionKey]: "" }, chatLoading: { ...s.chatLoading, [sectionKey]: true } }));
    try {
      const response = await fetch(AI_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: AI_MODEL, max_tokens: 1000, system: systemPrompt, messages: newMessages }),
      });
      const data = await response.json();
      const reply = data.content?.[0]?.text || "I'm having trouble responding right now — please try again.";
      this.setState(s => ({ chatMessages: { ...s.chatMessages, [sectionKey]: [...newMessages, { role: "assistant", content: reply }] }, chatLoading: { ...s.chatLoading, [sectionKey]: false } }));
    } catch (err) {
      this.setState(s => ({ chatLoading: { ...s.chatLoading, [sectionKey]: false } }));
      notification.error({ message: "Chat error — please try again" });
    }
  };

  sendAgentChat = async () => {
    const msg = (this.state.agentChatInput || "").trim();
    if (!msg) return;
    const { referringAgent, activeHomeId } = this.state;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId;
    const messages = [...this.state.agentChatMessages, { role: "user", content: msg, from: ud.firstName || "Client", timestamp: Date.now() }];
    this.setState({ agentChatMessages: messages, agentChatInput: "", agentChatLoading: true });
    try {
      await add("mybuddha", {
        entityData: {
          userId, homeId: activeHomeId, type: "agent_chat_message", sectionKey: "agent_chat",
          message: msg, fromName: `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
          fromEmail: ud.email || "", timestamp: Date.now(), toEmail: referringAgent?.email || "",
        },
      });
      if (referringAgent?.email) {
        await add("sendMail", {
          entityData: {
            to: referringAgent.email, from: ud.email || "noreply@myhomebuddha.com",
            subject: `💬 Home Portal Message from ${ud.firstName || "Your Client"}`,
            message: `${ud.firstName || "Your client"} sent you a message through their Home Buddha portal:\n\n"${msg}"\n\nLog in to respond: https://www.myhomebuddha.com`,
            isRegistred: true,
          },
        });
      }
      this.setState({ agentChatLoading: false });
      notification.success({ message: "Message sent to your agent!", duration: 3 });
    } catch(e) {
      console.error("sendAgentChat:", e);
      this.setState({ agentChatLoading: false });
      notification.error({ message: "Failed to send — please try again" });
    }
  };

  getAIDataForSection = (sectionKey) => {
    const { aiReport, aiReports } = this.state;
    const report = aiReport || (aiReports && aiReports[0]);
    if (!report) return null;
    const section = SECTIONS.find(s => s.key === sectionKey);
    if (!section) return null;
    const keys = [...(section.aiKeys || []), sectionKey];
    const findInObj = (obj) => { if (!obj || typeof obj !== "object") return null; for (const k of keys) { if (obj[k]) return obj[k]; } return null; };
    if (report.categoryData) { const found = findInObj(report.categoryData); if (found) return found; }
    if (report.reportData) {
      const rd = typeof report.reportData === "string" ? (() => { try { return JSON.parse(report.reportData); } catch(e) { return {}; } })() : report.reportData;
      if (rd.homeProfileResult) { const hpr = typeof rd.homeProfileResult === "string" ? (() => { try { return JSON.parse(rd.homeProfileResult); } catch(e) { return {}; } })() : rd.homeProfileResult; const found = findInObj(hpr); if (found) return found; }
      const found = findInObj(rd); if (found) return found;
    }
    if (report.homeProfileResult) { const hpr = typeof report.homeProfileResult === "string" ? (() => { try { return JSON.parse(report.homeProfileResult); } catch(e) { return {}; } })() : report.homeProfileResult; const found = findInObj(hpr); if (found) return found; }
    return null;
  };

  getSectionScore = (sectionKey) => {
    const aiData = this.getAIDataForSection(sectionKey);
    const manual = this.state.manualEntries[sectionKey] || [];
    if (!aiData && manual.length === 0) return null;
    return { score: aiData?.score || aiData?.rating || null, itemCount: manual.length, hasAI: !!aiData };
  };


  // ── PATCH 2: submitHelpRequest — wired to MissedLeads ───────────────────────
  submitHelpRequest = async () => {
    const { helpModal, helpType, helpNote } = this.state;
    if (!helpType) { notification.error({ message: "Please select what type of help you need" }); return; }
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const activeHome = this.state.homes.find(h => h.id === this.state.activeHomeId) || this.state.homes[0];
    this.setState({ helpSubmitting: true });
    try {
      await add("notifications", {
        entityData: {
          notificationType:   "vendor_request",
          leadType:           "vendor",
          source:             "home_portal",
          serviceCategory:    helpModal.sectionLabel,
          sectionKey:         helpModal.sectionKey,
          subsectionLabel:    helpModal.sectionLabel,
          helpType:           helpType,
          message:            helpNote || `Help needed with: ${helpModal.sectionLabel}`,
          address:            activeHome?.address || activeHome?.nickname || "",
          yearBuilt:          activeHome?.homeDetails?.yearBuilt || "",
          sqFootage:          activeHome?.homeDetails?.squareFeet || "",
          homeType:           activeHome?.homeDetails?.propertyType || "",
          requestorDetails: {
            name:   `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
            email:  ud.email || "",
            phone:  ud.phone || ud.phno || "",
          },
          isAssigned:     false,
          assignedStatus: "pending",
          createdAt:      Date.now(),
          userId:         ud.id || ud.userId || "",
        },
      });
      notification.success({
        message: "Request submitted!",
        description: "Our team will follow up with vendor options shortly.",
      });
      this.setState({ helpModal: null, helpType: "", helpNote: "", helpSubmitting: false });
    } catch(e) {
      notification.error({ message: "Failed to submit — please try again" });
      this.setState({ helpSubmitting: false });
    }
  };

  // ── PATCH 3: submitReminder — saves to DynamoDB + GHL webhook stub ───────────
  submitReminder = async () => {
    const { reminderModal, reminderDate, reminderTime, reminderRepeat, reminderNote } = this.state;
    if (!reminderDate) { notification.error({ message: "Please choose a reminder date" }); return; }
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const userId = ud.id || ud.userId || ud.userid;
    const activeHome = this.state.homes.find(h => h.id === this.state.activeHomeId) || this.state.homes[0];
    this.setState({ reminderSaving: true });

    const reminderPayload = {
      userId,
      type:            "home_reminder",
      action:          "home_reminder",
      sectionKey:      reminderModal.sectionKey,
      sectionLabel:    reminderModal.sectionLabel,
      subLabel:        reminderModal.subLabel || "",
      reminderDate,
      reminderTime,
      repeatFrequency: reminderRepeat,
      note:            reminderNote,
      address:         activeHome?.address || "",
      homeId:          this.state.activeHomeId || "",
      status:          "pending",
      createdAt:       Date.now(),
      // GHL-ready fields
      contactEmail:    ud.email || "",
      contactName:     `${ud.firstName || ""} ${ud.lastName || ""}`.trim(),
      contactPhone:    ud.phone || ud.phno || "",
    };

    try {
      await add("mybuddha", { entityData: reminderPayload });

      const GHL_REMINDER_WEBHOOK = process.env.REACT_APP_GHL_REMINDER_WEBHOOK || "";
      if (GHL_REMINDER_WEBHOOK) {
        try {
          await fetch(GHL_REMINDER_WEBHOOK, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event:           "home_reminder_created",
              contactEmail:    reminderPayload.contactEmail,
              contactName:     reminderPayload.contactName,
              contactPhone:    reminderPayload.contactPhone,
              reminderDate,
              reminderTime,
              repeatFrequency: reminderRepeat,
              sectionLabel:    reminderModal.sectionLabel,
              subLabel:        reminderModal.subLabel || "",
              note:            reminderNote,
              address:         reminderPayload.address,
            }),
          });
        } catch(webhookErr) {
          console.warn("[HomeBuddha] GHL reminder webhook failed (non-blocking):", webhookErr.message);
        }
      }

      notification.success({
        message: "Reminder set! ✅",
        description: reminderRepeat !== "none"
          ? `We'll remind you about ${reminderModal.subLabel || reminderModal.sectionLabel} on ${reminderDate} and ${reminderRepeat}.`
          : `Reminder set for ${reminderDate}.`,
      });
      this.setState({
        reminderModal: null, reminderDate: "", reminderTime: "09:00",
        reminderRepeat: "none", reminderNote: "", reminderSaving: false,
      });
    } catch(e) {
      console.error("submitReminder:", e);
      notification.error({ message: "Failed to save reminder — please try again" });
      this.setState({ reminderSaving: false });
    }
  };

  sendHomeownerInvite = async () => {
    const { inviteEmail, inviteName } = this.state;
    if (!inviteEmail.trim()) { notification.error({ message: "Please enter a client email" }); return; }
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const agentName = `${ud.firstName || ""} ${ud.lastName || ""}`.trim();
    this.setState({ inviteSending: true });
    try {
      const agBrand = JSON.parse(localStorage.getItem("mhb_agent_brand") || "{}");
      const invResult = await add("sendInviteEmail", {
        entityData: {
          toEmail:    inviteEmail.trim(),
          toName:     inviteName.trim() || inviteEmail.split("@")[0],
          fromName:   agentName,
          fromEmail:  ud.email || "",
          agentId:    ud.id || ud.userId || "",
          agentPhone: ud.phone || ud.phno || "",
          agentPhoto: agBrand.photo || "",
          agentCompany: agBrand.company || agBrand.brokerage || ud.newdata?.companyName || "",
          address: "", homeId: "", templateId: null, reports: [],
        },
      });
      notification.success({ message: `Invite sent to ${inviteEmail}!`, description: "They'll receive an email to create their account and home portal." });
      this.setState({ showInviteModal: false, inviteEmail: "", inviteName: "", inviteSending: false });
    } catch(e) {
      notification.error({ message: "Failed to send invite — please try again" });
      this.setState({ inviteSending: false });
    }
  };


  // ── PATCH 4: renderReminderModal ─────────────────────────────────────────────
  renderReminderModal() {
    const { reminderModal, reminderDate, reminderTime, reminderRepeat, reminderNote, reminderSaving } = this.state;
    if (!reminderModal) return null;
    const REPEAT_OPTIONS = [
      { key: "none",    label: "One time only" },
      { key: "weekly",  label: "Every week until done" },
      { key: "monthly", label: "Every month until done" },
    ];
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,30,0.78)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        onClick={() => this.setState({ reminderModal: null })}>
        <div style={{ background: WHITE, borderRadius: "20px", maxWidth: "420px", width: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: "linear-gradient(135deg,#1A2280,#2A3390)", padding: "18px 22px" }}>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "3px" }}>Set a Reminder</div>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: "17px" }}>⏰ {reminderModal.subLabel || reminderModal.sectionLabel}</div>
            {reminderModal.hcAnswer && (
              <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", marginTop: "4px" }}>{reminderModal.hcAnswer}</div>
            )}
          </div>
          <div style={{ padding: "18px 22px" }}>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>Remind me on *</div>
              <input type="date" value={reminderDate} min={new Date().toISOString().split("T")[0]}
                onChange={e => this.setState({ reminderDate: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px", fontFamily: "inherit" }} />
            </div>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>Time</div>
              <input type="time" value={reminderTime} onChange={e => this.setState({ reminderTime: e.target.value })}
                style={{ width: "100%", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "14px", fontFamily: "inherit" }} />
            </div>
            <div style={{ marginBottom: "14px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "8px" }}>How often?</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "7px" }}>
                {REPEAT_OPTIONS.map(opt => (
                  <div key={opt.key} onClick={() => this.setState({ reminderRepeat: opt.key })}
                    style={{ padding: "10px 14px", borderRadius: "10px", cursor: "pointer", border: `2px solid ${reminderRepeat === opt.key ? "#1A2280" : "#E2E8F0"}`, background: reminderRepeat === opt.key ? "#1A228010" : WHITE, display: "flex", alignItems: "center", gap: "10px", transition: "all .15s" }}>
                    <div style={{ width: "16px", height: "16px", borderRadius: "50%", flexShrink: 0, border: `2px solid ${reminderRepeat === opt.key ? "#1A2280" : "#CBD5E0"}`, background: reminderRepeat === opt.key ? "#1A2280" : "white", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {reminderRepeat === opt.key && <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "white" }} />}
                    </div>
                    <span style={{ fontSize: "13px", fontWeight: reminderRepeat === opt.key ? 700 : 400, color: reminderRepeat === opt.key ? "#1A2280" : "#2D3748" }}>{opt.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: "18px" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: "#718096", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>
                Note <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span>
              </div>
              <textarea value={reminderNote} onChange={e => this.setState({ reminderNote: e.target.value })}
                placeholder={`Add context about your ${reminderModal.subLabel || reminderModal.sectionLabel}...`}
                rows={3} style={{ width: "100%", padding: "9px 12px", border: "1px solid #E2E8F0", borderRadius: "8px", fontSize: "13px", fontFamily: "inherit", resize: "vertical" }} />
            </div>
            <div style={{ background: "#F0FFF4", border: "1px solid #9AE6B4", borderRadius: "8px", padding: "9px 12px", marginBottom: "16px", fontSize: "11px", color: "#276749" }}>
              📱 We'll send you a reminder email on {reminderDate || "your chosen date"}
              {reminderRepeat !== "none" ? ` and ${reminderRepeat} until marked complete` : ""}.
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => this.setState({ reminderModal: null })}
                style={{ flex: 1, padding: "10px", borderRadius: "50px", border: "1px solid #E2E8F0", background: "white", color: "#718096", fontSize: "13px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                Cancel
              </button>
              <button onClick={this.submitReminder} disabled={reminderSaving || !reminderDate}
                style={{ flex: 2, padding: "10px", borderRadius: "50px", border: "none", background: reminderDate ? "#1A2280" : "#E2E8F0", color: reminderDate ? "white" : "#A0AEC0", fontSize: "13px", fontWeight: 800, cursor: reminderDate ? "pointer" : "not-allowed", fontFamily: "inherit" }}>
                {reminderSaving ? "Saving..." : "⏰ Set Reminder"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderHelpModal() {
    const { helpModal, helpType, helpNote, helpSubmitting } = this.state;
    if (!helpModal) return null;
    const options = [
      { key: "done",   label: "🔧 Just Get It Done",       desc: "We find and coordinate a trusted vendor for you" },
      { key: "quote",  label: "📋 Get Me a Quote",          desc: "One vendor will contact you with a price" },
      { key: "quotes", label: "📊 Get Me Multiple Quotes",  desc: "2-3 vendors compete for your business" },
    ];
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,30,0.78)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        onClick={() => this.setState({ helpModal: null, helpType: "", helpNote: "" })}>
        <div style={{ background: WHITE, borderRadius: "20px", maxWidth: "420px", width: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, padding: "18px 22px" }}>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "3px" }}>Get Help With</div>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: "17px" }}>{helpModal.sectionLabel}</div>
          </div>
          <div style={{ padding: "18px 22px" }}>
            <div style={{ fontSize: "12px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>What do you need?</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "16px" }}>
              {options.map(opt => (
                <div key={opt.key} onClick={() => this.setState({ helpType: opt.key })}
                  style={{ border: `2px solid ${helpType === opt.key ? NAVY : BORDER}`, borderRadius: "10px", padding: "12px 14px", cursor: "pointer", background: helpType === opt.key ? `${NAVY}08` : WHITE, transition: "all .15s" }}>
                  <div style={{ fontWeight: 700, fontSize: "13px", color: NAVY, marginBottom: "2px" }}>{opt.label}</div>
                  <div style={{ fontSize: "11px", color: MUTED }}>{opt.desc}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>Add a Note <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></div>
            <Input.TextArea placeholder={`Describe what's needed with your ${helpModal.sectionLabel.toLowerCase()}...`} value={helpNote} onChange={e => this.setState({ helpNote: e.target.value })} rows={3} style={{ borderRadius: "8px", marginBottom: "14px" }} />
            <div style={{ display: "flex", gap: "10px" }}>
              <Button onClick={() => this.setState({ helpModal: null, helpType: "", helpNote: "" })} style={{ flex: 1, borderRadius: "50px" }}>Cancel</Button>
              <Button type="primary" loading={helpSubmitting} onClick={this.submitHelpRequest} style={{ flex: 2, background: NAVY, border: "none", borderRadius: "50px", fontWeight: 800 }}>Submit Request</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderInviteModal() {
    const { showInviteModal, inviteEmail, inviteName, inviteSending } = this.state;
    if (!showInviteModal) return null;
    const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const planRank = { free:0, basic:1, advanced:2, professional:3, team_leader:4 };
    const rank = planRank[ud.planTier || "free"] || 0;
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(10,12,30,0.78)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}
        onClick={() => this.setState({ showInviteModal: false })}>
        <div style={{ background: "white", borderRadius: "20px", maxWidth: "440px", width: "100%", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}
          onClick={e => e.stopPropagation()}>
          <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, padding: "20px 24px" }}>
            <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "4px" }}>Invite a Homeowner</div>
            <div style={{ color: "white", fontWeight: 800, fontSize: "17px" }}>📨 Build Their Home Portal</div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "12px", marginTop: "4px", lineHeight: 1.5 }}>Your client creates a free account and their Home Buddha portal is permanently branded to you.</div>
          </div>
          <div style={{ padding: "20px 24px" }}>
            {rank < 2 ? (
              <div style={{ textAlign: "center", padding: "12px 0" }}>
                <div style={{ fontSize: "32px", marginBottom: "12px" }}>🔒</div>
                <div style={{ fontWeight: 800, color: NAVY, fontSize: "15px", marginBottom: "8px" }}>Advanced Plan Required</div>
                <div style={{ fontSize: "13px", color: MUTED, lineHeight: 1.6, marginBottom: "20px" }}>Sending branded portal invites to clients requires the Advanced plan or higher.</div>
                <a href="?tab=subscriptions" style={{ display: "inline-block", background: NAVY, color: "white", textDecoration: "none", padding: "12px 28px", borderRadius: "50px", fontWeight: 800, fontSize: "13px" }}>View Upgrade Options →</a>
              </div>
            ) : (
              <>
                <div style={{ fontSize: "12px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>Client Details</div>
                <Input placeholder="Client name" value={inviteName} onChange={e => this.setState({ inviteName: e.target.value })} style={{ marginBottom: "8px", borderRadius: "8px" }} />
                <Input placeholder="Client email address" value={inviteEmail} onChange={e => this.setState({ inviteEmail: e.target.value })} style={{ marginBottom: "16px", borderRadius: "8px" }} />
                <div style={{ background: "#F7F9FC", borderRadius: "10px", padding: "12px 14px", marginBottom: "16px", fontSize: "12px", color: MUTED, lineHeight: 1.6 }}>
                  <strong style={{ color: NAVY }}>What your client receives:</strong><br/>
                  ✓ Branded invitation email with your name &amp; photo<br/>
                  ✓ Free account at myhomebuddha.com<br/>
                  ✓ Home portal pre-set with your branding forever<br/>
                  ✓ Access to all AI home tools — free
                </div>
                <div style={{ display: "flex", gap: "10px" }}>
                  <Button onClick={() => this.setState({ showInviteModal: false })} style={{ flex: 1, borderRadius: "50px" }}>Cancel</Button>
                  <Button type="primary" loading={inviteSending} onClick={this.sendHomeownerInvite} style={{ flex: 2, background: GOLD, border: "none", borderRadius: "50px", color: "#2A1600", fontWeight: 800 }}>📨 Send Invite</Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }


  renderAgentBrandingBanner() {
    const { referringAgent } = this.state;
    if (!referringAgent) return null;
    const name = `${referringAgent.firstName || ""} ${referringAgent.lastName || ""}`.trim() || referringAgent.email || "Your Agent";
    const photoKey = (referringAgent.profilePicture?.[0]) || (referringAgent.profilePicture2?.[0]) || null;
    const photo = photoKey
      ? `https://${process.env.REACT_APP_S3_BUCKET}.s3.amazonaws.com/public/${photoKey}`
      : (referringAgent.photo || null);
    const company = referringAgent.companyName || referringAgent.newdata?.companyName || referringAgent.title || "";
    const phone = referringAgent.phone || "";
    const email = referringAgent.email || "";
    const isLender = referringAgent.userProfile === "L";
    return (
      <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, borderRadius: "14px", padding: "14px 18px", marginBottom: "16px", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap" }}>
        {photo ? (
          <img src={photo} alt={name} style={{ width: "52px", height: "52px", borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(212,146,26,0.6)", flexShrink: 0 }} />
        ) : (
          <div style={{ width: "52px", height: "52px", borderRadius: "50%", background: "rgba(212,146,26,0.2)", border: "2px solid rgba(212,146,26,0.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>
            {isLender ? "💰" : "🏠"}
          </div>
        )}
        <div style={{ flex: 1, minWidth: "140px" }}>
          <div style={{ fontSize: "10px", fontWeight: 700, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "2px" }}>
            {isLender ? "Your Lender" : "Your Agent"} · Powered by My Home Buddha
          </div>
          <div style={{ color: WHITE, fontWeight: 800, fontSize: "15px" }}>{name}</div>
          {company && <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "12px" }}>{company}</div>}
        </div>
        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", flexShrink: 0 }}>
          {phone && (
            <a href={`tel:${phone}`} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50px", padding: "7px 14px", color: WHITE, fontSize: "12px", fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: "5px" }}>📞 {phone}</a>
          )}
          {email && (
            <a href={`mailto:${email}`} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "50px", padding: "7px 14px", color: WHITE, fontSize: "12px", fontWeight: 700, textDecoration: "none" }}>✉️ Email</a>
          )}
          <button onClick={() => this.setState({ agentChatOpen: true })}
            style={{ background: GOLD, border: "none", borderRadius: "50px", padding: "7px 14px", color: "#2A1600", fontSize: "12px", fontWeight: 800, cursor: "pointer" }}>
            💬 Ask {isLender ? "Lender" : "Agent"}
          </button>
        </div>
      </div>
    );
  }

  renderAgentChatPanel() {
    const { referringAgent, agentChatOpen, agentChatMessages, agentChatInput, agentChatLoading } = this.state;
    if (!referringAgent || !agentChatOpen) return null;
    const name = `${referringAgent.firstName || ""} ${referringAgent.lastName || ""}`.trim() || "Your Agent";
    const isLender = referringAgent.userProfile === "L";
    return (
      <div style={{ position: "fixed", bottom: "20px", right: "20px", width: "340px", maxHeight: "480px", background: WHITE, borderRadius: "16px", boxShadow: "0 8px 40px rgba(0,0,0,0.2)", border: `1px solid ${BORDER}`, display: "flex", flexDirection: "column", zIndex: 1000 }}>
        <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, borderRadius: "16px 16px 0 0", padding: "12px 16px", display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: WHITE, fontWeight: 800, fontSize: "13px" }}>💬 Ask {isLender ? "Your Lender" : "Your Agent"}</div>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: "11px" }}>{name} · via My Home Buddha</div>
          </div>
          <button onClick={() => this.setState({ agentChatOpen: false })} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: WHITE, borderRadius: "50%", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px" }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "14px", background: LIGHT, maxHeight: "300px" }}>
          {agentChatMessages.length === 0 && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <div style={{ fontSize: "28px", marginBottom: "8px" }}>🏠</div>
              <div style={{ fontSize: "12px", color: MUTED, lineHeight: 1.6 }}>Send a message to {name} — they'll be notified by email and can respond directly.</div>
              <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {["Can we schedule a walkthrough?", "I have questions about the inspection report", "What's the next step in the process?"].map((q, i) => (
                  <button key={i} onClick={() => this.setState({ agentChatInput: q })} style={{ textAlign: "left", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px 10px", fontSize: "12px", color: NAVY, cursor: "pointer" }}>{q}</button>
                ))}
              </div>
            </div>
          )}
          {agentChatMessages.map((msg, i) => (
            <div key={i} style={{ marginBottom: "10px", display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "85%", padding: "9px 12px", borderRadius: msg.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: msg.role === "user" ? NAVY : WHITE, color: msg.role === "user" ? WHITE : "#1A202C", fontSize: "12px", lineHeight: 1.6, border: msg.role === "assistant" ? `1px solid ${BORDER}` : "none" }}>{msg.content}</div>
            </div>
          ))}
          {agentChatLoading && (
            <div style={{ display: "flex", gap: "4px", padding: "6px 10px" }}>
              {[0, 0.2, 0.4].map(d => <div key={d} style={{ width: "6px", height: "6px", background: MUTED, borderRadius: "50%", animation: `pulse 1s ${d}s infinite` }} />)}
            </div>
          )}
        </div>
        <div style={{ padding: "10px 12px", borderTop: `1px solid ${BORDER}`, background: WHITE, borderRadius: "0 0 16px 16px" }}>
          <div style={{ fontSize: "11px", color: MUTED, marginBottom: "6px" }}>{name} will be notified by email</div>
          <div style={{ display: "flex", gap: "6px" }}>
            <Input value={agentChatInput} onChange={e => this.setState({ agentChatInput: e.target.value })} onPressEnter={this.sendAgentChat} placeholder={`Message ${name}...`} style={{ borderRadius: "8px", fontSize: "12px" }} />
            <Button type="primary" icon={<SendOutlined />} loading={agentChatLoading} onClick={this.sendAgentChat} style={{ background: NAVY, border: "none", borderRadius: "8px" }} />
          </div>
        </div>
      </div>
    );
  }

  renderReportsBanner() {
    const { savedReports, aiReport } = this.state;
    if (!savedReports || savedReports.length === 0) return null;
    const activeAddr = (aiReport?.address || "").toLowerCase().trim();
    const matchingReports = activeAddr ? savedReports.filter(r => (r.address || "").toLowerCase().trim() === activeAddr) : savedReports;
    if (matchingReports.length === 0) return null;
    return (
      <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "12px", marginBottom: "14px", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", padding: "12px 16px", cursor: "pointer", background: `${NAVY}06` }}
          onClick={() => this.setState(s => ({ reportsExpanded: !s.reportsExpanded }))}>
          <span style={{ fontSize: "18px" }}>📋</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 700, color: NAVY, fontSize: "13px" }}>{matchingReports.length} AI Report{matchingReports.length !== 1 ? "s" : ""} Saved</span>
            <span style={{ fontSize: "11px", color: MUTED, marginLeft: "8px" }}>Click to {this.state.reportsExpanded ? "hide" : "view"}</span>
          </div>
          <span style={{ color: MUTED, transition: "transform .2s", transform: this.state.reportsExpanded ? "rotate(180deg)" : "none" }}>⌄</span>
        </div>
        {this.state.reportsExpanded && (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            {matchingReports.map((r, i) => (
              <div key={r.id || i} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "10px 16px", borderBottom: i < matchingReports.length - 1 ? `1px solid ${BORDER}` : "none" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ fontWeight: 600, fontSize: "13px", color: "#1A202C" }}>{(r.reportType || r.recordType || "Report").replace(/_/g, " ")}</div>
                    {r.isLinked && <span style={{ fontSize: "10px", fontWeight: 700, color: GOLD, background: `${GOLD}15`, padding: "1px 7px", borderRadius: "50px", border: `1px solid ${GOLD}40` }}>🤝 Agent</span>}
                  </div>
                  <div style={{ fontSize: "11px", color: MUTED }}>{r.updatedAt ? new Date(r.updatedAt).toLocaleDateString() : ""}{r.address && r.address !== aiReport?.address ? ` · ${r.address}` : ""}</div>
                </div>
                <a href={`https://ai.myhomebuddha.com?loadReport=${r.id}&mode=${r.reportType === 'seller' || r.recordType === 'seller' ? 'seller' : r.reportType === 'homeowner' || r.recordType === 'homeowner' ? 'homeowner' : 'buyer'}`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: "11px", color: NAVY, fontWeight: 700, textDecoration: "none", background: `${NAVY}10`, borderRadius: "50px", padding: "4px 10px", flexShrink: 0 }}>View →</a>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  deleteHomeProfile = async (home) => {
    if (!window.confirm(`Remove portal for "${home.nickname || home.address || "this home"}"? The client will lose access to this portal. This cannot be undone.`)) return;
    try {
      await update("mybuddha", home.id, {
        entityData: { deleted: true, deletedAt: Date.now(), updatedAt: Date.now() }
      });
      notification.success({ message: "Home portal removed." });
      await this.loadAll();
    } catch(e) {
      notification.error({ message: "Delete failed — please try again" });
    }
  };

  renderOverview() {
    const { aiReport, manualEntries, homes, activeHomeId, referringAgent } = this.state;
    const totalManual = Object.values(manualEntries).reduce((a, b) => a + b.length, 0);
    const hasReport = !!aiReport;
    const activeHome = homes.find(h => h.id === activeHomeId) || null;
    return (
      <div>
        <div style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`, borderRadius: "16px", padding: "28px 24px", marginBottom: "20px", boxShadow: "0 6px 24px rgba(26,34,128,0.18)" }}>
          <div style={{ fontSize: "32px", marginBottom: "8px" }}>🏡</div>
          <h2 style={{ color: WHITE, fontWeight: 800, fontSize: "22px", margin: "0 0 6px" }}>Your Home Buddha Portal</h2>
          <p style={{ color: "rgba(255,255,255,0.75)", fontSize: "13px", margin: "0 0 16px", lineHeight: 1.6 }}>Your personal AI home health dashboard. Powered by your AI site analysis and your own notes.</p>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", textAlign: "center", minWidth: "90px" }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "22px" }}>{hasReport ? "✓" : "—"}</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>AI Report</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", textAlign: "center", minWidth: "90px" }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "22px" }}>{totalManual}</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>Notes Saved</div>
            </div>
            <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", textAlign: "center", minWidth: "90px" }}>
              <div style={{ color: GOLD, fontWeight: 800, fontSize: "22px" }}>6</div>
              <div style={{ color: "rgba(255,255,255,0.7)", fontSize: "11px", marginTop: "2px" }}>Categories</div>
            </div>
            {aiReport?.address && (
              <div style={{ background: "rgba(255,255,255,0.12)", borderRadius: "10px", padding: "12px 16px", flex: 1, minWidth: "160px" }}>
                <div style={{ color: GOLD, fontWeight: 700, fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "3px" }}>Property</div>
                <div style={{ color: WHITE, fontSize: "13px", fontWeight: 600 }}>{aiReport.address}</div>
              </div>
            )}
          </div>
          {!hasReport && (
            <div style={{ marginTop: "16px", background: "rgba(212,146,26,0.2)", borderRadius: "10px", padding: "12px 16px" }}>
              <div style={{ color: GOLD, fontWeight: 700, fontSize: "13px", marginBottom: "4px" }}>🤖 No AI Report Yet</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "12px", lineHeight: 1.5 }}>Run a Home Health Analysis on the AI site to populate this dashboard with personalized insights for your home.</div>
              <a href={"https://ai.myhomebuddha.com?autoLogin=1&userId=" + (JSON.parse(localStorage.getItem("userDetails") || "{}").id || "")} target="_blank" rel="noopener noreferrer"
                style={{ display: "inline-block", marginTop: "10px", background: GOLD, color: "#2A1600", borderRadius: "50px", padding: "7px 16px", fontWeight: 700, fontSize: "12px", textDecoration: "none" }}>
                Go to AI Site →
              </a>
            </div>
          )}
        </div>
{/* ── Health Check banners ───────────────────────────────────────── */}
        {this.renderHCBanner()}
        {/* ── Health Check banners ───────────────────────────────────────── */}
        {this.renderHCBanner()}
        {this.renderHCScoreBanner()}

        {/* ── Read-only referred-by row ────────────────────────── */}
        {referringAgent && (() => {
          const ra = referringAgent;
          const raName = `${ra.firstName || ""} ${ra.lastName || ""}`.trim() || ra.email || "Your Agent";
          const raIsLender = ra.userProfile === "L";
          return (
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "10px", padding: "10px 14px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "10px", justifyContent: "space-between" }}>
              <div style={{ fontSize: "12px", color: "#4A5568" }}>
                <span style={{ color: "#718096", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em" }}>
                  {raIsLender ? "Referred by Lender" : "Referred by Agent"}&nbsp;·&nbsp;
                </span>
                <span style={{ fontWeight: 700, color: NAVY }}>{raName}</span>
                {ra.email && <span style={{ color: "#718096", fontSize: "11px", marginLeft: "8px" }}>{ra.email}</span>}
              </div>
              <div style={{ fontSize: "10px", color: "#A0AEC0", flexShrink: 0 }}>🔒 To update, contact support</div>
            </div>
          );
        })()}

        {this.renderReportsBanner()}

        {activeHome?.coUsers?.length > 0 && (
          <div style={{ background: WHITE, borderRadius: "12px", border: `1px solid ${BORDER}`, padding: "14px 16px", marginBottom: "16px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px" }}>Home Portal Access</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {activeHome.coUsers.map((u, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", background: LIGHT, borderRadius: "50px", padding: "5px 12px 5px 6px", border: `1px solid ${BORDER}` }}>
                  <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: NAVY, color: WHITE, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700 }}>{(u.name || "?")[0].toUpperCase()}</div>
                  <span style={{ fontSize: "12px", fontWeight: 600, color: "#1A202C" }}>{u.name}</span>
                  <span style={{ fontSize: "10px", color: MUTED, background: "#E2E8F0", borderRadius: "50px", padding: "1px 6px" }}>{u.role}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <Row gutter={[14, 14]}>
          {SECTIONS.map(section => {
            const manual = manualEntries[section.key] || [];
            const aiData = this.getAIDataForSection(section.key);
            return (
              <Col key={section.key} xs={24} sm={12} lg={8}>
                <Card style={{ borderRadius: "12px", border: `1px solid ${BORDER}` }} bodyStyle={{ padding: "16px" }}>
                  <div onClick={() => this.setState({ activeKey: section.key })} style={{ cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                      <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: `${section.color}18`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px" }}>{section.emoji}</div>
                      <div style={{ flex: 1 }}><div style={{ fontWeight: 700, fontSize: "13px", color: "#1A202C", lineHeight: 1.2 }}>{section.label}</div></div>
                    </div>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "10px" }}>
                      {aiData && <Tag color="blue" style={{ fontSize: "10px", borderRadius: "50px" }}>🤖 AI Data</Tag>}
                      {manual.length > 0 && <Tag color="green" style={{ fontSize: "10px", borderRadius: "50px" }}>{manual.length} note{manual.length !== 1 ? "s" : ""}</Tag>}
                      {!aiData && manual.length === 0 && <Tag style={{ fontSize: "10px", borderRadius: "50px", color: MUTED }}>Empty — click to add</Tag>}
                    </div>
                  </div>
                  <button
                    onClick={e => { e.stopPropagation(); this.setState({ helpModal: { sectionKey: section.key, sectionLabel: section.label }, helpType: "", helpNote: "" }); }}
                    style={{ width: "100%", background: `${section.color}12`, border: `1px solid ${section.color}30`, borderRadius: "8px", padding: "6px 10px", color: section.color, fontSize: "11px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textAlign: "center" }}>
                    🛠 I Need Help With This
                  </button>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  }


  // ── PATCH 5: renderSubsection with 3-button row (added Set Reminder) ────────
  renderSubsection(section, sub) {
    const subKey = section.key + ":" + sub.id;
    const isOpen = this.state.openSubsections[subKey] || false;
    const { subChatMessages, subChatInput, subChatLoading, subAnalyzing, subAnalysis, manualEntries } = this.state;
    const msgs        = subChatMessages[subKey] || [];
    const chatVal     = subChatInput[subKey]    || "";
    const isLoading   = subChatLoading[subKey]  || false;
    const isAnalyzing = subAnalyzing[subKey]    || false;
    const analysis    = subAnalysis[subKey]     || "";
    const manual      = manualEntries[section.key] || [];
    const hasData     = analysis || msgs.length > 0 || manual.length > 0;
    return (
      <div key={sub.id} style={{ border: `1px solid ${isOpen ? section.color + "40" : BORDER}`, borderRadius: "12px", marginBottom: "10px", overflow: "hidden", transition: "border-color .2s", boxShadow: isOpen ? `0 2px 12px ${section.color}15` : "none" }}>
        <div onClick={() => {
            const opening = !isOpen;
            this.setState(s => ({ openSubsections: { ...s.openSubsections, [subKey]: opening } }));
            this.syncUrl(section.key, opening ? sub.id : null);
          }}
          style={{ display: "flex", alignItems: "center", gap: "12px", padding: "14px 16px", cursor: "pointer", background: isOpen ? `${section.color}08` : WHITE, transition: "background .2s" }}>
          <span style={{ fontSize: "22px", flexShrink: 0 }}>{sub.emoji}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>{sub.label}</div>
            <div style={{ fontSize: "11px", color: MUTED, marginTop: "1px" }}>{sub.hint}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {(() => {
              const hcEntry = (manualEntries[section.key] || []).find(e => e.hcQuestionId === sub.id);
              if (!hcEntry) return null;
              const ratio = hcEntry.hcScore / (hcEntry.hcMax || 4);
              const mood = ratio >= 0.75 ? "😊" : ratio >= 0.5 ? "🙂" : ratio >= 0.25 ? "😐" : "😟";
              return <span title={"Health Check score: " + hcEntry.description} style={{ fontSize: "18px", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.15))" }}>{mood}</span>;
            })()}
            {hasData && <span style={{ fontSize: "10px", background: `${section.color}20`, color: section.color, borderRadius: "50px", padding: "2px 8px", fontWeight: 700 }}>Has data</span>}
            <span style={{ color: MUTED, fontSize: "18px", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
          </div>
        </div>
        {isOpen && (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            <Row gutter={0}>
              <Col xs={24} lg={14} style={{ borderRight: `1px solid ${BORDER}`, padding: "16px" }}>
                <div style={{ background: "linear-gradient(135deg,#F0FFF4,#E6FFFA)", border: "1px solid #9AE6B4", borderRadius: "10px", padding: "10px 14px", marginBottom: "14px" }}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: "#276749", marginBottom: "4px" }}>📸 Add Photos for Better Insights</div>
                  <div style={{ fontSize: "11px", color: "#4A5568", lineHeight: 1.6, marginBottom: "10px" }}>Take or upload photos of your {sub.label.toLowerCase()} — our AI can help identify age, condition, issues to watch for, and maintenance tips.</div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "#276749", color: "white", borderRadius: "50px", fontSize: "12px", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                      📷 Take Photo
                      <input type="file" accept="image/*" capture="environment" style={{ display: "none" }}
                        onChange={e => { if (e.target.files && e.target.files[0]) { this.handleSectionSubmit({ sectionKey: section.key, subsectionId: sub.id, description: "Photo of " + sub.label, photos: [e.target.files[0]] }); } }} />
                    </label>
                    <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", padding: "8px 14px", background: "white", color: "#276749", border: "1px solid #9AE6B4", borderRadius: "50px", fontSize: "12px", fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                      🖼️ Upload Photo
                      <input type="file" accept="image/*" multiple style={{ display: "none" }}
                        onChange={e => { if (e.target.files && e.target.files.length > 0) { this.handleSectionSubmit({ sectionKey: section.key, subsectionId: sub.id, description: "Photos of " + sub.label, photos: Array.from(e.target.files) }); } }} />
                    </label>
                  </div>
                </div>
                {analysis && (
                  <div style={{ background: `${NAVY}06`, borderRadius: "10px", padding: "14px", marginBottom: "14px", border: `1px solid ${NAVY}15` }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "8px" }}>🤖 AI Analysis</div>
                    <div style={{ fontSize: "13px", color: "#2D3748", lineHeight: 1.7, whiteSpace: "pre-line" }}>{analysis}</div>
                  </div>
                )}
                {(() => {
                  const hcEntry = (manualEntries[section.key] || []).find(e => e.hcQuestionId === sub.id);
                  if (!hcEntry) return null;
                  const ratio = hcEntry.hcScore / (hcEntry.hcMax || 4);
                  const color = ratio >= 0.75 ? "#38A169" : ratio >= 0.5 ? "#3182CE" : ratio >= 0.25 ? "#D97706" : "#C53030";
                  const mood = ratio >= 0.75 ? "😊" : ratio >= 0.5 ? "🙂" : ratio >= 0.25 ? "😐" : "😟";
                  return (
                    <div style={{ background: color + "10", border: "1px solid " + color + "30", borderRadius: "10px", padding: "10px 14px", marginBottom: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                        <span style={{ fontSize: "16px" }}>{mood}</span>
                        <span style={{ fontSize: "11px", fontWeight: 700, color: color, textTransform: "uppercase", letterSpacing: ".04em" }}>Health Check Answer</span>
                      </div>
                      <div style={{ fontSize: "12px", color: "#2D3748", lineHeight: 1.5 }}>{hcEntry.description}</div>
                      {hcEntry.notes && <div style={{ fontSize: "11px", color: "#718096", marginTop: "4px", fontStyle: "italic" }}>"{hcEntry.notes}"</div>}
                      <div style={{ fontSize: "10px", color: "#A0AEC0", marginTop: "6px" }}>From your Home Health Check-Up · Add photos below to get a more accurate score</div>
                    </div>
                  );
                })()}

                {/* ── 3-button action row ─────────────────────── */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
                   <Button type={analysis ? "default" : "primary"} loading={isAnalyzing}
                    onClick={() => this.analyzeSubsection(section.key, sub)}
                    style={{ borderRadius: "50px", fontWeight: 700, background: analysis ? WHITE : NAVY, borderColor: analysis ? BORDER : NAVY, color: analysis ? NAVY : WHITE }}
                    icon={<RobotOutlined />}>
                    {analysis ? "Re-analyze" : "Analyze " + sub.label}
                  </Button>
                  <Button
                    onClick={() => {
                      const hcEntry = (manualEntries[section.key] || []).find(e => e.hcQuestionId === sub.id);
                      this.setState({
                        reminderModal: {
                          sectionKey:   section.key,
                          sectionLabel: section.label,
                          subLabel:     sub.label,
                          hcAnswer:     hcEntry ? hcEntry.description : null,
                        },
                        reminderDate: "", reminderTime: "09:00", reminderRepeat: "none", reminderNote: "",
                      });
                    }}
                    style={{ borderRadius: "50px", fontWeight: 700, background: "#F0FFF4", border: "1px solid #9AE6B4", color: "#276749" }}>
                    ⏰ Set Reminder
                  </Button>
                  <Button
                    onClick={() => this.setState({ helpModal: { sectionKey: section.key, sectionLabel: sub.label }, helpType: "", helpNote: "" })}
                    style={{ borderRadius: "50px", fontWeight: 700, background: `${section.color}12`, border: `1px solid ${section.color}40`, color: section.color }}
                    icon={<ToolOutlined />}>
                    🛠 I Need Help
                  </Button>
                </div>
                  <ChecklistForm title={sub.label} points={[sub.hint]}
                  onSectionSubmit={(values, recordId) => { this.setState({ activeKey: section.key }); this.handleSectionSubmit({ ...values, sectionKey: section.key, subsectionId: sub.id }, recordId); }}
                  onSectionDelete={this.handleSectionDelete}
                  submittedData={manual.filter(e => e.subsectionId === sub.id)} />
              </Col>
              <Col xs={24} lg={10} style={{ padding: "0", display: "flex", flexDirection: "column" }}>
                <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, padding: "12px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <RobotOutlined style={{ color: GOLD, fontSize: "16px" }} />
                    <div>
                      <div style={{ color: WHITE, fontWeight: 700, fontSize: "12px" }}>{sub.label + " Advisor"}</div>
                      <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "10px" }}>Ask specific questions about this area</div>
                    </div>
                  </div>
                </div>
                <div style={{ overflowY: "auto", maxHeight: "200px", padding: "12px 14px", background: LIGHT }}>
                  {msgs.length === 0 && (
                    <div style={{ textAlign: "center", padding: "16px 0" }}>
                      <div style={{ fontSize: "20px", marginBottom: "6px" }}>{sub.emoji}</div>
                      <div style={{ fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>{"Ask anything about " + sub.label.toLowerCase() + " for your home."}</div>
                      <div style={{ marginTop: "10px", display: "flex", flexDirection: "column", gap: "5px" }}>
                        {["What should I know about my " + sub.label.toLowerCase() + "?", "How do I maintain my " + sub.label.toLowerCase() + "?", "What are warning signs I should watch for?"].map((q, i) => (
                          <button key={i} onClick={() => this.setState(s => ({ subChatInput: { ...s.subChatInput, [subKey]: q } }), () => this.sendSubChat(section.key, sub.id))}
                            style={{ textAlign: "left", background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "7px 10px", fontSize: "11px", color: NAVY, cursor: "pointer", lineHeight: 1.4 }}>{q}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {msgs.map((msg, i) => (
                    <div key={i} style={{ marginBottom: "10px", display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                      <div style={{ maxWidth: "88%", padding: "8px 11px", borderRadius: msg.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px", background: msg.role === "user" ? NAVY : WHITE, color: msg.role === "user" ? WHITE : "#1A202C", fontSize: "11px", lineHeight: 1.6, border: msg.role === "assistant" ? `1px solid ${BORDER}` : "none" }}>{msg.content}</div>
                    </div>
                  ))}
                  {isLoading && <div style={{ display: "flex", gap: "4px", padding: "6px 10px" }}>{[0,.2,.4].map(d => <div key={d} style={{ width: "5px", height: "5px", background: MUTED, borderRadius: "50%", animation: `pulse 1s ${d}s infinite` }} />)}</div>}
                </div>
                <div style={{ padding: "10px 12px", borderTop: `1px solid ${BORDER}`, background: WHITE }}>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <TextArea value={chatVal} onChange={e => this.setState(s => ({ subChatInput: { ...s.subChatInput, [subKey]: e.target.value } }))}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.sendSubChat(section.key, sub.id); } }}
                      placeholder={"Ask about " + sub.label.toLowerCase() + "..."} autoSize={{ minRows: 1, maxRows: 3 }} style={{ borderRadius: "8px", fontSize: "11px", resize: "none" }} />
                    <Button type="primary" icon={<SendOutlined />} loading={isLoading} onClick={() => this.sendSubChat(section.key, sub.id)} style={{ background: NAVY, border: "none", borderRadius: "8px", height: "auto", minHeight: "32px" }} />
                  </div>
                </div>
                <div style={{ padding: "8px 8px 0", flexShrink: 0 }}>
                  <div style={{ background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, borderRadius: "8px 8px 0 0", padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "16px" }}>🛒</span>
                      <div>
                        <div style={{ color: WHITE, fontWeight: 700, fontSize: "12px" }}>Available Products &amp; Services</div>
                        <div style={{ color: "rgba(255,255,255,0.6)", fontSize: "10px" }}>Trusted picks for this area</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ border: `1px solid ${BORDER}`, borderTop: "none", borderRadius: "0 0 8px 8px", padding: "12px 10px" }}>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textAlign: "center", marginBottom: "4px", textTransform: "uppercase", letterSpacing: ".03em" }}>
                      🛒 Click on image to order
                    </div>
                    <div style={{ borderRadius: "8px", overflow: "hidden", maxHeight: "180px", marginBottom: "12px" }}>
                      <Advertisement otherAds={false} adType="Square" adWhereToShow={"HomeBuddha_" + sub.id + "_product"} adLimit="1" zipCode="" detailsPageAds={true} cityName={this.getActiveHomeGeo().cityName} stateName={this.getActiveHomeGeo().stateName} postalCode={this.getActiveHomeGeo().postalCode} />
                    </div>
                    <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textAlign: "center", marginBottom: "4px", textTransform: "uppercase", letterSpacing: ".03em" }}>
                      🤝 Click on image to request service
                    </div>
                    <div style={{ borderRadius: "8px", overflow: "hidden", maxHeight: "180px" }}>
                      <Advertisement otherAds={false} adType="Square" adWhereToShow={"HomeBuddha_" + sub.id + "_service"} adLimit="1" zipCode="" detailsPageAds={true} cityName={this.getActiveHomeGeo().cityName} stateName={this.getActiveHomeGeo().stateName} postalCode={this.getActiveHomeGeo().postalCode} />
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    );
  }


  renderSection(section) {
    const { chatMessages, chatInput, chatLoading, aiReport } = this.state;
    const messages      = chatMessages[section.key] || [];
    const chatVal       = chatInput[section.key] || "";
    const isChatLoading = chatLoading[section.key] || false;
    const hasSubsections = section.subsections && section.subsections.length > 0;
    const starters = {
      home_health:           ["What are the biggest health risks in my home?", "How do I improve indoor air quality?", "What should I check for moisture damage?"],
      energy_efficiency:     ["How can I reduce my energy bills this winter?", "What's the best insulation upgrade for my budget?", "Should I get solar panels?"],
      maintenance_longevity: ["What maintenance should I do this season?", "How do I know when to replace my roof?", "What deferred maintenance is most urgent?"],
      other_spaces:          ["How should I winterize my outdoor spaces?", "What should I look for in my garage?", "How do I maintain my yard for home value?"],
      stress_reduction:      ["What should I focus on first?", "What can I safely ignore for now?", "How do I automate home maintenance reminders?"],
      value_wealth:          ["What improvements add the most value?", "How do I prepare my home for sale?", "What cosmetic updates have best ROI?"],
      notes_collaboration:   ["Summarize my home notes so far", "What follow-ups do I have outstanding?", "Help me organize my home tasks"],
    };
    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px", padding: "16px 20px", background: `linear-gradient(135deg, ${NAVY}08, transparent)`, borderRadius: "12px", borderLeft: `4px solid ${section.color}` }}>
          <span style={{ fontSize: "28px" }}>{section.emoji}</span>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, color: NAVY, fontWeight: 800, fontSize: "17px" }}>{section.label}</h3>
            <div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>{hasSubsections ? `${section.subsections.length} areas · click any area below to expand` : ""}</div>
          </div>
          {!aiReport?.address && (
            <a href={"https://ai.myhomebuddha.com?autoLogin=1&userId=" + (JSON.parse(localStorage.getItem("userDetails") || "{}").id || "")} target="_blank" rel="noopener noreferrer"
              style={{ fontSize: "11px", color: NAVY, background: `${GOLD}20`, borderRadius: "50px", padding: "4px 12px", fontWeight: 700, textDecoration: "none", flexShrink: 0 }}>🤖 Run AI Analysis</a>
          )}
          {this.state.referringAgent && (
            <button onClick={() => this.setState({ agentChatOpen: true })}
              style={{ background: GOLD, border: "none", borderRadius: "50px", padding: "5px 12px", color: "#2A1600", fontSize: "11px", fontWeight: 800, cursor: "pointer", flexShrink: 0 }}>
              💬 Ask {this.state.referringAgent.userProfile === "L" ? "Your Lender" : "Your Agent"}
            </button>
          )}
        </div>
        <div style={{ marginBottom: "20px", border: `1px solid ${NAVY}25`, borderRadius: "12px", overflow: "hidden" }}>
          <div onClick={() => this.setState(s => ({ [`chat_open_${section.key}`]: !s[`chat_open_${section.key}`] }))}
            style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", background: `linear-gradient(135deg, ${NAVY}, #2A3390)`, cursor: "pointer" }}>
            <RobotOutlined style={{ color: GOLD, fontSize: "16px" }} />
            <div style={{ flex: 1 }}>
              <span style={{ color: WHITE, fontWeight: 700, fontSize: "13px" }}>{section.label} Advisor</span>
              <span style={{ color: "rgba(255,255,255,0.55)", fontSize: "11px", marginLeft: "8px" }}>{messages.length > 0 ? `${messages.length} message${messages.length !== 1 ? "s" : ""}` : "Ask broad questions about this category"}</span>
            </div>
            <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "16px", transform: this.state[`chat_open_${section.key}`] ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
          </div>
          {this.state[`chat_open_${section.key}`] && (
            <div>
              <div style={{ maxHeight: "260px", overflowY: "auto", padding: "14px 16px", background: LIGHT }}>
                {messages.length === 0 && (
                  <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    {(starters[section.key] || []).map((q, i) => (
                      <button key={i} onClick={() => this.setState(s => ({ chatInput: { ...s.chatInput, [section.key]: q } }), () => this.sendChat(section.key))}
                        style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px 12px", fontSize: "12px", color: NAVY, cursor: "pointer", lineHeight: 1.4, fontWeight: 500 }}>{q}</button>
                    ))}
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} style={{ marginBottom: "10px", display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
                    <div style={{ maxWidth: "75%", padding: "10px 13px", borderRadius: msg.role === "user" ? "14px 14px 2px 14px" : "14px 14px 14px 2px", background: msg.role === "user" ? NAVY : WHITE, color: msg.role === "user" ? WHITE : "#1A202C", fontSize: "12px", lineHeight: 1.6, border: msg.role === "assistant" ? `1px solid ${BORDER}` : "none" }}>{msg.content}</div>
                  </div>
                ))}
                {isChatLoading && <div style={{ display: "flex", gap: "4px", padding: "8px 12px" }}>{[0,.2,.4].map(d => <div key={d} style={{ width: "6px", height: "6px", background: MUTED, borderRadius: "50%", animation: `pulse 1s ${d}s infinite` }} />)}</div>}
              </div>
              <div style={{ padding: "10px 14px", borderTop: `1px solid ${BORDER}`, background: WHITE }}>
                <div style={{ display: "flex", gap: "8px" }}>
                  <TextArea value={chatVal} onChange={e => this.setState(s => ({ chatInput: { ...s.chatInput, [section.key]: e.target.value } }))}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); this.sendChat(section.key); } }}
                    placeholder="Ask about this section..." autoSize={{ minRows: 1, maxRows: 3 }} style={{ borderRadius: "8px", fontSize: "12px", resize: "none" }} />
                  <Button type="primary" icon={<SendOutlined />} loading={isChatLoading} onClick={() => this.sendChat(section.key)} style={{ background: NAVY, border: "none", borderRadius: "8px", height: "auto", minHeight: "36px" }} />
                </div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "4px" }}>Enter to send · Shift+Enter for new line</div>
              </div>
            </div>
          )}
        </div>
        {hasSubsections ? <div>{section.subsections.map(sub => this.renderSubsection(section, sub))}</div> : (
          <ChecklistForm title={section.label} points={section.points} onSectionSubmit={this.handleSectionSubmit} onSectionDelete={this.handleSectionDelete} submittedData={this.state.manualEntries[section.key] || []} />
        )}
        <div style={{ marginTop: "20px", background: "linear-gradient(135deg,#744210,#9A5A00)", borderRadius: "14px", padding: "18px 20px", display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <div style={{ fontSize: "32px", flexShrink: 0 }}>🏅</div>
          <div style={{ flex: 1, minWidth: "200px" }}>
            <div style={{ color: "#FFD98E", fontWeight: 800, fontSize: "14px", marginBottom: "4px" }}>Unlock Your Final 10% — Book a Certified Home Health Inspection</div>
            <div style={{ color: "rgba(255,255,255,0.8)", fontSize: "12px", lineHeight: 1.6 }}>A certified My Home Buddha Home Health Inspector visits your home, verifies every system, and uncovers what you cannot see.</div>
          </div>
          <a href="https://go.myhomebuddha.com/homeowner" target="_blank" rel="noopener noreferrer"
            style={{ flexShrink: 0, padding: "10px 20px", background: "#D4921A", color: "#2A1600", borderRadius: "50px", fontWeight: 800, fontSize: "13px", textDecoration: "none", whiteSpace: "nowrap" }}>📞 Book Now</a>
        </div>
      </div>
    );
  }


  renderSetupModal() {
    const { setupStep, setupMode, setupAddress, setupPublicData, setupEdits, setupLoading, savedReports, selectedReport } = this.state;
    const isOpen = this.props.showSetupModal || false;
    const merged = { ...(setupPublicData || {}), ...setupEdits };
    const FIELDS = [
      { key: "yearBuilt", label: "Year Built" }, { key: "squareFeet", label: "Square Feet" },
      { key: "bedrooms", label: "Bedrooms" }, { key: "bathrooms", label: "Bathrooms" },
      { key: "lotSize", label: "Lot Size" }, { key: "propertyType", label: "Property Type" },
      { key: "stories", label: "Stories" }, { key: "garageSpaces", label: "Garage Spaces" },
      { key: "heatingType", label: "Heating Type" }, { key: "coolingType", label: "Cooling/AC Type" },
      { key: "roofType", label: "Roof Type" }, { key: "foundationType", label: "Foundation Type" },
      { key: "estimatedValue", label: "Estimated Value" },
    ];
    const steps = ["Choose Source", "Enter Address", "Confirm Details", "Analyze"];
    return (
      <Modal visible={isOpen}
        title={<div style={{ display: "flex", alignItems: "center", gap: "10px" }}><span style={{ fontSize: "20px" }}>🏡</span><span style={{ color: NAVY, fontWeight: 800 }}>Create a Home Profile</span></div>}
        onCancel={() => { if (this.props.onCloseSetupModal) this.props.onCloseSetupModal(); this.setState({ setupStep: 0, setupMode: null, setupAddress: "", setupPublicData: null, setupEdits: {} }); }}
        footer={null} width={680} centered bodyStyle={{ minHeight: "380px" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: "24px" }}>
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1 }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: i < setupStep ? "#38A169" : i === setupStep ? NAVY : "#E2E8F0", color: i <= setupStep ? "white" : MUTED, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "13px", marginBottom: "4px", transition: "all .2s" }}>{i < setupStep ? "✓" : i + 1}</div>
                <div style={{ fontSize: "10px", fontWeight: i === setupStep ? 700 : 500, color: i === setupStep ? NAVY : MUTED, textAlign: "center", whiteSpace: "nowrap" }}>{s}</div>
              </div>
              {i < steps.length - 1 && <div style={{ flex: 2, height: "2px", marginBottom: "18px", background: i < setupStep ? "#38A169" : "#E2E8F0", transition: "background .2s" }} />}
            </React.Fragment>
          ))}
        </div>

        {setupStep === 0 && (
          <div>
            <div style={{ fontSize: "14px", color: MUTED, marginBottom: "16px", lineHeight: 1.6 }}>How would you like to set up your home profile?</div>
            {(() => {
              let pendingCapture = null;
              try { pendingCapture = JSON.parse(localStorage.getItem("mhb_pending_home") || "null"); } catch(e) {}
              if (!pendingCapture || !pendingCapture.address) return null;
              return (
                <Card hoverable onClick={() => { this.setState({ setupMode: "address", setupStep: 1, setupAddress: pendingCapture.address }); this.fetchPublicRecords(pendingCapture.address); }}
                  style={{ marginBottom: "12px", borderRadius: "12px", cursor: "pointer", border: `2px solid ${GOLD}`, background: `${GOLD}08` }} bodyStyle={{ padding: "18px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ fontSize: "32px" }}>✨</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Continue Where You Left Off</div>
                      <div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>Finish setting up {pendingCapture.address}</div>
                    </div>
                    <div style={{ fontSize: "20px", color: MUTED }}>→</div>
                  </div>
                </Card>
              );
            })()}
            {(JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "AG" || JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "L") && (
              <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                {["self", "client"].map(opt => (
                  <button key={opt} onClick={() => {
                    if (opt === "client") {
                      const ud = JSON.parse(localStorage.getItem("userDetails") || "{}");
                      const planRank = { free:0, basic:1, advanced:2, professional:3, team_leader:4 };
                      const rank = planRank[ud.planTier || "free"] || 0;
                      if (rank < 2) { notification.warning({ message: "Advanced Plan Required", description: "Setting up client portals requires the Advanced plan.", duration: 6 }); return; }
                    }
                    this.setState({ setupFor: opt });
                  }}
                    style={{ flex: 1, padding: "10px", borderRadius: "10px", cursor: "pointer", fontWeight: 700, fontSize: "13px", border: "2px solid", borderColor: this.state.setupFor === opt ? NAVY : BORDER, background: this.state.setupFor === opt ? `${NAVY}10` : WHITE, color: this.state.setupFor === opt ? NAVY : MUTED, transition: "all .15s" }}>
                    {opt === "self" ? "🏠 For My Home" : "👤 For a Client"}
                  </button>
                ))}
              </div>
            )}
            {this.state.setupFor === "client" && (
              <div style={{ background: `${NAVY}08`, borderRadius: "10px", padding: "14px", marginBottom: "14px", border: `1px solid ${NAVY}20` }}>
                <div style={{ fontSize: "12px", fontWeight: 700, color: NAVY, marginBottom: "10px" }}>Client Details — their portal is permanently branded to you</div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "6px" }}>Select Existing Client</div>
                <Select showSearch allowClear placeholder={this.state.myClients.length > 0 ? `Search ${this.state.myClients.length} clients...` : "No clients yet — enter below"}
                  style={{ width: "100%", marginBottom: "10px" }}
                  filterOption={(input, option) => (option.props.children || "").toLowerCase().includes(input.toLowerCase())}
                  onChange={v => {
                    if (!v) { this.setState({ clientName: "", clientEmail: "" }); return; }
                    const client = this.state.myClients.find(c => c.id === v);
                    if (client) this.setState({ clientName: (`${client.firstName || ""} ${client.lastName || ""}`).trim(), clientEmail: client.email || "" });
                  }}>
                  {this.state.myClients.map(c => (
                    <Select.Option key={c.id} value={c.id}>{(`${c.firstName || ""} ${c.lastName || ""}`).trim() + (c.email ? " — " + c.email : "")}</Select.Option>
                  ))}
                </Select>
                <div style={{ textAlign: "center", fontSize: "11px", color: MUTED, marginBottom: "8px" }}>— or enter a new client —</div>
                <Input placeholder="Client name" value={this.state.clientName} onChange={e => this.setState({ clientName: e.target.value })} style={{ marginBottom: "8px", borderRadius: "8px" }} />
                <Input placeholder="Client email address" value={this.state.clientEmail} onChange={e => this.setState({ clientEmail: e.target.value })} style={{ marginBottom: this.state.clientEmail ? "10px" : "0", borderRadius: "8px" }} />
                 <Input placeholder="Property address (optional)" value={this.state.setupAddress} onChange={e => this.setState({ setupAddress: e.target.value })} style={{ marginTop: "8px", borderRadius: "8px" }} />
 
                {/* ── Value Tracker Setup (optional, for closings) ───────────── */}
                <div onClick={() => this.setState(s => ({ vtExpanded: !s.vtExpanded }))}
                  style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "12px", padding: "10px 12px", background: "#FFFBF0", border: "1px solid #F0D98A", borderRadius: "8px", cursor: "pointer" }}>
                  <span style={{ fontSize: "16px" }}>💰</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "12px", fontWeight: 700, color: "#744210" }}>Value Tracker Setup (optional)</div>
                    <div style={{ fontSize: "10px", color: MUTED }}>At closing? Add purchase price, mortgage & rate — we'll pre-fill their Value Tracker.</div>
                  </div>
                  <span style={{ color: MUTED, fontSize: "14px", transform: this.state.vtExpanded ? "rotate(180deg)" : "none", transition: "transform .2s" }}>⌄</span>
                </div>
                {this.state.vtExpanded && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px", padding: "12px", background: "#FFFBF0", border: "1px solid #F0D98A", borderRadius: "8px" }}>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Purchase Price</div>
                      <Input placeholder="e.g. 450000" value={this.state.vtPurchasePrice} onChange={e => this.setState({ vtPurchasePrice: e.target.value })} size="small" style={{ borderRadius: "6px" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Closing Date</div>
                      <Input type="date" value={this.state.vtPurchaseDate} onChange={e => this.setState({ vtPurchaseDate: e.target.value })} size="small" style={{ borderRadius: "6px" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Mortgage Balance</div>
                      <Input placeholder="e.g. 405000" value={this.state.vtMortgageBalance} onChange={e => this.setState({ vtMortgageBalance: e.target.value })} size="small" style={{ borderRadius: "6px" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Interest Rate</div>
                      <Input placeholder="e.g. 6.75" value={this.state.vtMortgageRate} onChange={e => this.setState({ vtMortgageRate: e.target.value })} size="small" style={{ borderRadius: "6px" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Loan Type</div>
                      <Select value={this.state.vtMortgageType} onChange={v => this.setState({ vtMortgageType: v })} size="small" style={{ width: "100%" }}>
                        {["30yr fixed","20yr fixed","15yr fixed","10yr fixed","5/1 ARM","7/1 ARM"].map(t => <Select.Option key={t} value={t}>{t}</Select.Option>)}
                      </Select>
                    </div>
                    <div>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Down Payment</div>
                      <Input placeholder="e.g. 45000" value={this.state.vtDownPayment} onChange={e => this.setState({ vtDownPayment: e.target.value })} size="small" style={{ borderRadius: "6px" }} />
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      <div style={{ fontSize: "10px", fontWeight: 700, color: MUTED, textTransform: "uppercase", marginBottom: "3px" }}>Annual Property Tax</div>
                      <Input placeholder="e.g. 6200" value={this.state.vtPropertyTax} onChange={e => this.setState({ vtPropertyTax: e.target.value })} size="small" style={{ borderRadius: "6px" }} />
                    </div>
                  </div>
                )}
                {this.state.clientEmail && (
                  <div style={{ background: `${GOLD}18`, borderRadius: "8px", padding: "9px 12px", border: `1px solid ${GOLD}35`, marginTop: "10px" }}>
                    <span style={{ fontSize: "11px", color: "#744210", fontWeight: 600 }}>Just want to invite without a property?&nbsp;</span>
                    <button onClick={() => { this.setState({ inviteName: this.state.clientName, inviteEmail: this.state.clientEmail }); if (this.props.onCloseSetupModal) this.props.onCloseSetupModal(); setTimeout(() => this.setState({ showInviteModal: true }), 100); }}
                      style={{ background: "none", border: "none", color: NAVY, fontSize: "11px", fontWeight: 800, cursor: "pointer", textDecoration: "underline", padding: 0 }}>Send invite only →</button>
                  </div>
                )}
              </div>
            )}
            {savedReports.length === 0 && (
              <div style={{ background: "#FFF8E6", borderRadius: "10px", padding: "14px 16px", marginBottom: "12px", border: "1px solid #F6D860", fontSize: "13px", color: "#7A5010", lineHeight: 1.5 }}>
                <strong>No saved AI reports yet.</strong> After running a Home Health Analysis on the AI site, your report will appear here.{" "}
                <a href="https://ai.myhomebuddha.com" target="_blank" rel="noopener noreferrer" style={{ color: "#1A2280", fontWeight: 700 }}>Go to AI site →</a>
              </div>
            )}
            {savedReports.length > 0 && (
              <Card hoverable onClick={() => this.setState({ setupMode: "saved", setupStep: 1 })}
                style={{ marginBottom: "12px", borderRadius: "12px", cursor: "pointer", border: `2px solid ${setupMode === "saved" ? NAVY : BORDER}` }} bodyStyle={{ padding: "18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ fontSize: "32px" }}>📋</div>
                  <div style={{ flex: 1 }}><div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Use a Saved AI Report</div><div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>Attach one of your {savedReports.length} saved report{savedReports.length !== 1 ? "s" : ""} from the AI site</div></div>
                  <div style={{ fontSize: "20px", color: MUTED }}>→</div>
                </div>
              </Card>
            )}
            <Card hoverable onClick={() => this.setState({ setupMode: "address", setupStep: 1 })}
              style={{ borderRadius: "12px", cursor: "pointer", border: `2px solid ${setupMode === "address" ? NAVY : BORDER}` }} bodyStyle={{ padding: "18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ fontSize: "32px" }}>📍</div>
                <div style={{ flex: 1 }}><div style={{ fontWeight: 700, color: NAVY, fontSize: "14px" }}>Enter My Address</div><div style={{ fontSize: "12px", color: MUTED, marginTop: "2px" }}>Pull public records for your home and build a profile from scratch</div></div>
                <div style={{ fontSize: "20px", color: MUTED }}>→</div>
              </div>
            </Card>
          </div>
        )}

        {setupStep === 1 && setupMode === "saved" && (
          <div>
            <div style={{ fontWeight: 700, color: NAVY, marginBottom: "12px" }}>Select a Saved Report</div>
            {savedReports.map(r => (
              <Card key={r.id} hoverable
                onClick={() => { this.setState({ selectedReport: r, setupStep: 1, setupMode: "address", setupAddress: r.address || r.addr || "" }); }}
                style={{ marginBottom: "10px", borderRadius: "10px", border: `2px solid ${selectedReport?.id === r.id ? NAVY : BORDER}`, cursor: "pointer" }} bodyStyle={{ padding: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ fontWeight: 600, color: NAVY, fontSize: "13px" }}>📍 {r.address}</div>
                  {(r.reportType || r.recordType) && <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "50px", background: `${NAVY}15`, color: NAVY, textTransform: "capitalize", flexShrink: 0, marginLeft: "8px" }}>{(r.reportType || r.recordType || "").replace(/_/g, " ")}</span>}
                </div>
                {r.updatedAt && <div style={{ fontSize: "11px", color: MUTED, marginTop: "3px" }}>Last updated {new Date(r.updatedAt).toLocaleDateString()}</div>}
              </Card>
            ))}
            <Button onClick={() => this.setState({ setupStep: 0 })} style={{ marginTop: "8px" }}>← Back</Button>
          </div>
        )}

        {setupStep === 1 && setupMode === "address" && (
          <div>
            <div style={{ fontWeight: 700, color: NAVY, marginBottom: "8px" }}>Enter Your Home Address</div>
            <Input.Search value={setupAddress} onChange={e => this.setState({ setupAddress: e.target.value })} placeholder="123 Main St, City, State ZIP" enterButton="Look Up Records" size="large" loading={setupLoading}
              onSearch={addr => { if (!addr.trim()) { notification.error({ message: "Please enter an address" }); return; } this.fetchPublicRecords(addr); }} style={{ marginBottom: "8px" }} />
            <div style={{ fontSize: "11px", color: MUTED, lineHeight: 1.5 }}>We'll pull publicly available property records and let you review and correct them before saving.</div>
            <Button onClick={() => this.setState({ setupStep: 0 })} style={{ marginTop: "16px" }}>← Back</Button>
          </div>
        )}

        {setupStep === 2 && (
          <div>
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontWeight: 700, color: NAVY, fontSize: "14px", marginBottom: "4px" }}>📍 {merged.address || setupAddress || selectedReport?.address}</div>
              <div style={{ fontSize: "12px", color: MUTED }}>Review the information below and correct anything that's wrong.</div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "16px" }}>
              {FIELDS.map(({ key, label }) => (
                <div key={key}>
                  <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "3px" }}>{label}</div>
                  <Input value={setupEdits[key] !== undefined ? setupEdits[key] : (merged[key] || "")} onChange={e => this.setState(s => ({ setupEdits: { ...s.setupEdits, [key]: e.target.value } }))} placeholder={"Enter " + label.toLowerCase()} size="small" style={{ borderRadius: "6px" }} />
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <Button onClick={() => this.setState({ setupStep: 1 })}>← Back</Button>
              <Button type="primary" style={{ background: NAVY, border: "none", borderRadius: "50px", fontWeight: 700, flex: 1 }} onClick={() => this.setState({ setupStep: 3 })}>Looks Good — Continue →</Button>
            </div>
          </div>
        )}

        {setupStep === 3 && (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🤖</div>
            <div style={{ fontWeight: 800, color: NAVY, fontSize: "18px", marginBottom: "8px" }}>Ready to Build Your Home Profile</div>
            <div style={{ fontSize: "13px", color: MUTED, marginBottom: "24px", lineHeight: 1.6, maxWidth: "400px", margin: "0 auto 24px" }}>We'll save your home details and open your Home Buddha portal.</div>
            <div style={{ background: LIGHT, borderRadius: "10px", padding: "14px 16px", marginBottom: "20px", textAlign: "left" }}>
              <div style={{ fontSize: "11px", fontWeight: 700, color: GOLD, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "8px" }}>Your Home Summary</div>
              {[["Address", merged.address || setupAddress || selectedReport?.address], ["Year Built", merged.yearBuilt], ["Size", merged.squareFeet ? merged.squareFeet + " sq ft" : null], ["Beds / Baths", merged.bedrooms && merged.bathrooms ? `${merged.bedrooms} bed / ${merged.bathrooms} bath` : null], ["Type", merged.propertyType]].filter(([, v]) => v).map(([label, value]) => (
                <div key={label} style={{ display: "flex", gap: "8px", fontSize: "12px", marginBottom: "4px" }}>
                  <span style={{ color: MUTED, minWidth: "80px" }}>{label}:</span>
                  <span style={{ color: "#1A202C", fontWeight: 600 }}>{value}</span>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              <Button onClick={() => this.setState({ setupStep: 2 })}>← Edit Details</Button>
              <Button type="primary" loading={setupLoading} onClick={this.saveAndAnalyze} style={{ background: GOLD, border: "none", borderRadius: "50px", fontWeight: 800, fontSize: "14px", height: "42px", padding: "0 28px", color: "#2A1600" }}>
                🚀 Save & Open My Home Portal
              </Button>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  render() {
    const { loading, activeKey } = this.state;
    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: "60px 20px", background: WHITE }}>
          <Spin size="large" />
          <div style={{ color: MUTED, marginTop: "16px", fontSize: "13px" }}>Loading your home portal...</div>
        </div>
      );
    }
    return (
      <div style={{ background: LIGHT, minHeight: "600px" }}>
        <div style={{ padding: "16px 16px 0" }}>
          {this.renderClaimYourHomeProgress()}
        </div>
        {(JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "AG" ||
          JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "L") && (
          <div style={{ display: "flex", gap: "10px", padding: "14px 16px 10px", flexWrap: "wrap" }}>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { if (this.props.onShowSetupModal) this.props.onShowSetupModal(); }}
              style={{ borderRadius: "50px", background: NAVY, border: "none", fontWeight: 700, flex: 1 }}>
              🏠 Create a Home Profile
            </Button>
            <Button onClick={() => this.setState({ showInviteModal: true })}
              style={{ borderRadius: "50px", background: GOLD, border: "none", color: "#2A1600", fontWeight: 700, flex: 1 }}>
              📨 Invite a Homeowner
            </Button>
          </div>
        )}

        {this.state.homes.length > 0 && (
          <div style={{ padding: "4px 16px 0" }}>
            <div style={{ fontSize: "13px", fontWeight: 800, color: NAVY, marginBottom: "8px", textTransform: "uppercase", letterSpacing: ".04em" }}>🏡 My Home Portals</div>
            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
              {this.state.homes.map(h => (
              <div key={h.id} style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
                <button onClick={() => this.setState({ activeHomeId: h.id, aiReport: h })}
                  style={{ padding: "5px 12px", paddingRight: "28px", borderRadius: "50px", cursor: "pointer", fontWeight: 600, fontSize: "12px", border: "2px solid", borderColor: this.state.activeHomeId === h.id ? NAVY : BORDER, background: this.state.activeHomeId === h.id ? NAVY : WHITE, color: this.state.activeHomeId === h.id ? WHITE : "#1A202C", transition: "all .15s" }}>
                  🏡 {h.nickname || (h.address || "Home").split(",")[0]}
                </button>
                {(JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "AG" || JSON.parse(localStorage.getItem("userDetails") || "{}").userProfile === "L") && (
                  <button onClick={e => { e.stopPropagation(); this.deleteHomeProfile(h); }}
                    title="Remove this portal"
                    style={{ position: "absolute", right: "6px", background: "none", border: "none", cursor: "pointer", color: this.state.activeHomeId === h.id ? "rgba(255,255,255,0.6)" : "#CBD5E0", fontSize: "11px", lineHeight: 1, padding: "2px" }}>✕</button>
                )}
              </div>
            ))}
            </div>
          </div>
        )}
        <Tabs activeKey={activeKey} onChange={key => { this.setState({ activeKey: key }); this.syncUrl(key, null); }}
          tabBarStyle={{ padding: "0 16px", background: WHITE, marginBottom: 0 }}
          tabBarExtraContent={<Button size="small" icon={<ReloadOutlined />} onClick={this.loadAll} style={{ borderRadius: "50px", color: MUTED, border: `1px solid ${BORDER}`, marginRight: "8px" }}>Refresh</Button>}>
          <TabPane tab={<span>🏠 Overview</span>} key="overview">
            <div style={{ padding: "16px" }}>
              {this.renderAgentBrandingBanner()}
              {this.renderOverview()}
            </div>
          </TabPane>
          {SECTIONS.map(section => (
            <TabPane key={section.key} tab={
              <span>
                {section.emoji} {section.label.split(" ")[0]}
                {this.getSectionScore(section.key) && (
                  <span style={{ marginLeft: "4px", fontSize: "10px", background: `${section.color}20`, color: section.color, borderRadius: "50px", padding: "1px 6px" }}>
                    {this.getAIDataForSection(section.key) ? "AI" : ""}
                    {(this.state.manualEntries[section.key] || []).length > 0 ? `${this.getAIDataForSection(section.key) ? "+" : ""}${this.state.manualEntries[section.key].length}` : ""}
                  </span>
                )}
              </span>
            }>
              <div style={{ padding: "16px" }}>{this.renderSection(section)}</div>
            </TabPane>
          ))}
        </Tabs>
        <style>{`@keyframes pulse { 0%, 100% { opacity: 0.3; transform: scale(0.8); } 50% { opacity: 1; transform: scale(1); } }`}</style>
        {this.renderSetupModal()}
        {this.renderAgentChatPanel()}
        {this.renderInviteModal()}
        {this.renderHelpModal()}
        {this.renderHCModal()}
        {/* PATCH 6: renderReminderModal registered here */}
        {this.renderReminderModal()}
      </div>
    );
  }
}

export default withRouter(HomeBuddha);